import { missingInputDetail } from '../janus-engine/telemetry/missing-input-detail';
import { mutableSurface } from '../janus-engine/compile/mutable-surface';
import { parsedAppliedFrame, appliedFramePlanMetrics } from '../janus-engine/transport/applied-frame-plan';
import { appliedTreeMetrics, appliedListSlots } from '../janus-engine/transport/applied-tree-routing';
import { applyAppliedFrame, paintAppliedTree } from '../janus-engine/transport/apply-applied-frame';
import type { AppliedFrame, AppliedFrameResult, AppliedTouchedRow } from '../janus-engine/transport/applied-frame';
import { appliedDefinitionIds, withAppliedDefinitionInputs } from '../janus-engine/transport/applied-definition-inputs';
import type { SavedRowCorrection } from '../janus-engine/transport/action-bar-saved-rows';
import { appliedInventoryUnchanged } from '../janus-engine/transport/applied-inventory-change';
import { createCompileWorker } from './compile-worker';
import { copyTree } from '../janus-engine/compile/tree-transfer';
import { yieldBetweenMemberCompiles } from '../janus-engine/compile/compile-task';
import { BaseRevisionSuperseded, baseEntityKey, baseEntityKind, createBaseRuntime } from '../janus-engine/compile/base-runtime';
import { spawnCompileInput, spawnInputReadChunks, type SpawnTemplateNeeds } from '../janus-engine/spawn/birth-inputs';
import { catalogReadError, recoverBaseCatalog } from '../janus-engine/compile/catalog-recovery';
import type { BaseRuntimeHooks } from '../janus-engine/compile/base-runtime';
import type { BaseBusy, BaseEnvelope, BasePatchFrame } from '../janus-engine/compile/base-patch';
import { prepareInventoryDisplay } from '../janus-engine/compile/inventory-display';
import { patchAuraOwnerSkillLeaves } from '../janus-engine/compile/aura-owner-skill-projection';
import { validateEnchantment } from '../janus-engine/compile/validate-enchantment';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers';
import { confirmInventoryBirth } from '../shared/inventory-birth-persistence';
import { refreshStatusInputs } from '../janus-engine/compile/base-patch';
import { memberStatusInputs } from '../janus-engine/compile/member-status-inputs';
import { attachInventoryCatalog, inventoryCatalogScope, mergeInventoryMetadata, type InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import { createIdleCacheWriter, yieldForCacheWrite } from './cache-write-task';
import { refuseUnported, skipUnported, noteEngineReload, ENGINE_RELOAD_MESSAGE } from '../janus-engine/unported';
import { paintEntitySurface, prepareEntitySurface, prepareCachedEntitySurface, entitySurfacePreparationMetrics } from '../janus-engine/compile/entity-display';
import { encounterShapeOnlyRow } from '../janus-engine/compile/encounter-shape-delta';
import { doorOpeningShapeRow } from '../janus-engine/transport/door-opening-frame';
import { isTruthCorrectionPending } from './movement-truth';
import { sharedEntityComputed } from '../janus-engine/compile/entity-saved-sections';
import { projectNewbornControl } from './newborn-control';
import { encounterCreatureField, encounterField } from '../shared/array-types';
import { readCombatEncounterHeader } from '../janus-engine/combat/encounter-header';
import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { compileWorkerRecoveryToken, createCompileVersionRecovery, isCompileEngineMismatch } from '../shared/compile-version-recovery';
import { loadRelocationMembers } from './relocation-members';
import { withSharedDefinitionOps } from '../janus-engine/compile/shared-definition-ops';
import { readSavedResponse, fetchSavedResponse } from '../shared/read-saved-response';
import { ftCurrentTruthSink, isTruthRequest, truthFetch } from '../shared/truth-sink';
import type { SavedReadResponse } from '../shared/read-saved-response';
import { encounterMutationGeneration } from './encounter-mutation-generation';
import { markStall } from '../shared/stall-mark';
import { markFt3dEventDelayDiagnostic } from '../shared/ft3d-event-delay-diagnostic';
import { beginSpawnSave, reconcileSpawnSaves } from '../shared/spawn-save-pending';
// This display importer still consumes the C5-owned from-base surface reader.
// Keep its compile inputs present in both shipping identity graphs until C5 separates that reader.
import '../janus-engine/port/k5-render/encounter-display';
import { appliedCasterViewChanges } from '../shared/caster-view-changes';

type Row = Record<string, unknown>;
const hostWindow = window as Window & {
    ftCompileFetchedEntity?: typeof compileFetchedEntity;
    ftAdoptInventoryBase?: (host: unknown, options?: { transient?: boolean; detached?: boolean }) => Promise<Row | void>;
    ftApplyBasePatch?: (frame: BasePatchFrame, actor?: boolean) => Promise<void>;
    ftBaseCommitsPaused?: () => boolean;
    ftRepaintCasterFromCache?: (id: string) => void;
    ftMovementTruthConfirmed?: (encounterId: string, actorId: string) => { rev: number } | null;
};
declare const __FT_COMPILE_ENGINE_HASH__: string;
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { savedSkillGrantPatch, type SavedSkillGrantPatch } from '../janus-engine/compile/saved-skill-grant-delta';
import { memberDefinitionInputs } from '../janus-engine/compile/member-status-inputs';
import { encounterMemberSharedInputs } from '../janus-engine/compile/encounter-member-input';
import { planPreparedSpellDelta, patchPreparedSpellProjection, preparedSpellRows } from '../janus-engine/compile/prepared-spell-delta';
import type { PreparedSpellDelta } from '../janus-engine/compile/prepared-spell-delta';
import type { FromBasePlayerInput } from '../janus-engine/compile/from-base-context';
const engine = __FT_COMPILE_ENGINE_HASH__;
import { floorEnchantmentOnlyRow, patchFloorEnchantment } from '../janus-engine/compile/floor-enchantment-delta';
import { encounterEnvironmentOnlyRow } from '../janus-engine/compile/encounter-environment-delta';
import { handleEncounterStatsUpdate } from './encounter-stats-update';
import { patchCampaignPartyPlayers } from '../janus-engine/compile/saved-party-player-delta';
import { _ftCamField } from '../shared/campaign-field';
const campaignPartyRevisions = new Map<string, Map<string, number>>();

function projectCampaignPartyPlayers(ops: readonly import('../janus-engine/compile/base-patch').BasePatchOp[]): void {
    if (!ops.some(op => op.table === 'player_characters')) return;
    const ft = window.FateTide;
    const members = [...ft?.casterCache?.values() ?? [], ...[...ft?.encounterCache?.values() ?? []]
        .flatMap(host => host.encounter_creatures_arr ?? [])] as Row[];
    for (const campaign of ft?.campaignCache?.values() ?? []) {
        const campaignId = String(_ftCamField(campaign, 'campaign_id') ?? '').toUpperCase();
        let revisions = campaignPartyRevisions.get(campaignId);
        if (!revisions) { revisions = new Map(); campaignPartyRevisions.set(campaignId, revisions); }
        if (patchCampaignPartyPlayers(campaign, ops, members, revisions)) window.dispatchEvent(new CustomEvent('ft:campaign-state-received', {
            detail: { campaignId, campaignArr: campaign },
        }));
    }
}
const workerUrl = new URL('includes/js/dist/janus-compile-worker-bundle.js', new URL(BASE_URL, location.href));
workerUrl.searchParams.set('v', engine);
const recoveryToken = compileWorkerRecoveryToken(engine);
if (recoveryToken) workerUrl.searchParams.set('recovery', recoveryToken);
let lastWorkerEngine: string | undefined;
const newCompiler = (): ReturnType<typeof createCompileWorker> => createCompileWorker(engine, workerUrl.href, undefined, undefined,
    next => { lastWorkerEngine = next; handleWorkerVersionChange(next); });
const entityCompiler = newCompiler();
window.ftCompileLongRestChain = entityCompiler.longRest;
window.ftPreviewSpawnSource = entityCompiler.previewSpawn;
const relocationCompilers: ReturnType<typeof createCompileWorker>[] = [];
async function compileRelocationBatch(entities: readonly import('../janus-engine/transport/applied-frame').AppliedNewEntity[],
    metadata: import('../janus-engine/spawn/source-plan').SpawnSourcePackage['metadata']): Promise<import('../janus-engine/compile/saved-creature-relocation').RelocationMemberBatch> {
    const width = Math.min(4, entities.length);
    const batches = await Promise.all(Array.from({ length: width }, async (_, lane) => {
        const batch = entities.filter((_, index) => index % width === lane);
        try {
            const compiler = lane === 0 ? entityCompiler : relocationCompilers[lane - 1]
                ?? (relocationCompilers[lane - 1] = newCompiler());
            const result = await compiler.previewSpawn({ entities: batch, metadata, isolatedDefinitions: true, partial: true });
            if (!Array.isArray(result.new_entities)) throw new Error('relocation-member-batch:compile');
            const members = result.new_entities as import('../janus-engine/transport/applied-frame').AppliedNewEntity[];
            const failures = Array.isArray(result.failures) ? result.failures as import('../janus-engine/compile/saved-creature-relocation').RelocationMemberFailure[] : [];
            return { members, failures: batch.filter(entity => !members.some(member => member.entity_id === entity.entity_id)).map(entity => ({
                id: entity.entity_id, reason: failures.find(failure => failure.id === entity.entity_id)?.reason ?? 'relocation-member-batch:compile',
            })) };
        } catch (error) {
            return { members: [], failures: batch.map(entity => ({ id: entity.entity_id,
                reason: error instanceof Error ? error.message : 'relocation-member-batch:compile' })) };
        }
    }));
    const completed = new Map(batches.flatMap(batch => batch.members.map(entity => [entity.entity_id, entity] as const)));
    return { members: entities.flatMap(entity => completed.has(entity.entity_id) ? [completed.get(entity.entity_id)!] : []),
        failures: batches.flatMap(batch => batch.failures) };
}
/** Boot the bounded relocation lanes while the movers' bases are still being read (a 10-member batch read answers in ~3 s on staging),
 *  so the compile batch pays no worker start: a cold lane cost 0.5–0.8 s on the 2026-10-28 served pull. Idempotent; a lane that fails
 *  to start reports on its first compile as before. */
function warmRelocationCompilers(count: number): void {
    const width = Math.min(4, Math.max(1, Math.trunc(count) || 1));
    entityCompiler.warm();
    for (let lane = 1; lane < width; lane++) (relocationCompilers[lane - 1] ??= newCompiler()).warm();
}
Object.assign(hostWindow, { ftWarmRelocationCompilers: warmRelocationCompilers });
declare global { interface Window { ftCompileLongRestChain: typeof entityCompiler.longRest } }
(window as unknown as { ftDecodeSavedJson: (json: string | ArrayBuffer) => Promise<unknown> }).ftDecodeSavedJson = entityCompiler.catalog;
hostWindow.ftCompileFetchedEntity = compileFetchedEntity;
const surfaces = new Map<string, Set<Row>>();
const actorWrites = new Set<string>();
const adoptedSurfaces = new WeakMap<Row, BaseEnvelope | BaseBusy>();
type SurfaceAdoption = { promise: Promise<Row | void>; entity: unknown; inventory: unknown; carried: unknown };
const adoptionJobs = new WeakMap<Row, SurfaceAdoption>();
const hydrationJobs = new WeakMap<BaseEnvelope | BaseBusy, Promise<BaseEnvelope | BaseBusy>>();
const savedBases = new WeakMap<BaseEnvelope['base'], BaseEnvelope['base']>();
const catalogs = new Map<string, Promise<InventorySharedCatalog>>();
const catalogRevisions = new Map<string, { revision: number; key: string }>();
const expiredCatalogVersions = new Set<string>();
const globalCatalogs = new Map<string, InventorySharedCatalog>();
const definitions = new Map<string, Row>();
const definitionInputs = new WeakMap<Row, Row>();
/** The raw SELECT closure the page already holds for one definition — a catalog read, an adopted object or the definition store — for a
 *  local truth sink whose held base has no PHP read to recover a status item's definition from (training grounds, tgstale28c). */
export function heldDefinitionInputs(id: string, engine?: string | null): Row | undefined {
    const key = id.toUpperCase();
    for (const catalog of definitions.values()) {
        const raw = catalog[key];
        if (fromBaseRecord(raw)) { const inputs = definitionInputs.get(raw); if (inputs) return inputs; }
    }
    const store = (window as unknown as { __ftDefinitionStore?: { peek?(id: string): { def: Row } | null } }).__ftDefinitionStore;
    for (const held of [window.FateTide?.objectCache?.get(key), store?.peek?.(key)?.def]) {
        const source = (held as Row | undefined)?._entity_base as BaseEnvelope | undefined;
        if (source && (!engine || source.engine === engine)) return source.base;
    }
    return undefined;
}
const memberDefinitionReads = new Map<string, Promise<void>>();
function fetchMemberDefinitions(ids: readonly string[], version: string | null | undefined): Promise<void> {
    const store = (window as unknown as { __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } }).__ftDefinitionStore;
    const key = (id: string): string => JSON.stringify([version, store?.epoch, store?.cacheGeneration, id]);
    const missing = ids.filter(id => !memberDefinitionReads.has(key(id)));
    if (missing.length) {
        const read = (window as unknown as { ftFetchObjectsBatch?: (ids: readonly string[]) => Promise<void> }).ftFetchObjectsBatch;
        const job = read ? read(missing) : Promise.reject(new Error('status-definition-reader-unavailable'));
        for (const id of missing) memberDefinitionReads.set(key(id), job);
        // Failed joins must reach the reader again; its bounded cooldown owns retries.
        void job.catch(() => { for (const id of missing) memberDefinitionReads.delete(key(id)); });
        while (memberDefinitionReads.size > 512) memberDefinitionReads.delete(memberDefinitionReads.keys().next().value!);
    }
    return Promise.all(ids.map(id => memberDefinitionReads.get(key(id))!)).then(() => {});
}
// Source ids have no revision in a compact birth. This existing SELECT endpoint
// returns raw definitions and media/template witnesses without compiling objects.
const spawnDefinitionReads = new Map<string, Promise<{ base: Row; metadata: unknown }>>();
function readSpawnDefinitions(ids: readonly string[], version: string | null | undefined, templates: SpawnTemplateNeeds = { enchantment_template_ids: [], monster_template_ids: [] }): Promise<{ base: Row; metadata: unknown }> {
    const store = (window as unknown as { __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } }).__ftDefinitionStore;
    const key = JSON.stringify([version, store?.epoch, store?.cacheGeneration, [...ids].sort(), [...templates.enchantment_template_ids].sort(), [...templates.monster_template_ids].sort()]);
    const previous = spawnDefinitionReads.get(key);
    if (previous) return previous;
    const pending = (async () => {
        let base: Row = {}, metadata: unknown;
        for (let chunk = 0; chunk < spawnInputReadChunks(ids, templates); chunk++) {
            const start = chunk * 128;
            let timer: ReturnType<typeof setTimeout> | undefined;
            // Bound the fetch AND decode, including transports that ignore abort.
            // Each chunk retains its own full window; a failed GM Add read leaves
            // the cache just like a failed strict-birth bank.
            const selected = await Promise.race([(async () => {
                const response = await fetchSavedResponse(`${BASE_URL}options/api/spawn_inputs_inc.php`, {
                    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
                    signal: AbortSignal.timeout(20000),
                    body: JSON.stringify({ ids: ids.slice(start, start + 128),
                        enchantment_template_ids: templates.enchantment_template_ids.slice(start, start + 128),
                        monster_template_ids: templates.monster_template_ids.slice(start / 4, start / 4 + 32) }),
                });
                if (!response.ok) throw new Error(`spawn:definition-read:${response.status}`);
                const selected = await readSavedResponse(response);
                if (!fromBaseRecord(selected) || !fromBaseRecord(selected.base) || !fromBaseRecord(selected.metadata))
                    throw new Error('spawn:definition-read-shape');
                return selected;
            })(), new Promise<never>((_resolve, reject) => {
                timer = setTimeout(() => reject(new Error('spawn:input-banking-timeout')), 20000);
            })]).finally(() => clearTimeout(timer));
            base = mergeInventoryMetadata(base, mergeInventoryMetadata(fromBaseRecord(selected.shared) ? selected.shared : {}, selected.base));
            metadata = selected.metadata;
        }
        return { base, metadata };
    })();
    spawnDefinitionReads.set(key, pending);
    void pending.catch(() => { if (spawnDefinitionReads.get(key) === pending) spawnDefinitionReads.delete(key); });
    while (spawnDefinitionReads.size > 128) spawnDefinitionReads.delete(spawnDefinitionReads.keys().next().value!);
    return pending;
}
window.ftReadSpawnInputs = readSpawnDefinitions;
declare global { interface Window { ftReadSpawnInputs?: typeof readSpawnDefinitions } }
const entitySurfaces = new Map<string, Set<Row>>();
const adoptingEntitySurfaces = new Map<string, Set<Row>>();
const encounterPaintGenerations = new WeakMap<Row, number>();
const encounterReadGenerations = new WeakMap<Row, number>();
const memberInventories = new WeakMap<Row, Map<Row, string>>();
const memberInventoryOwners = new WeakMap<Row, Set<Row>>();
const watchedEncounterCaches = new WeakSet<object>();
type CatalogWaiter = { resolve(value: InventorySharedCatalog): void; reject(error: unknown): void };
const catalogQueue = new Map<string, Map<string, CatalogWaiter>>();
/** Every adoption in flight, by the entity ids its host carries, with the revision each answers for. PHP's
 * `unported:<entity>-base-pending` is the placeholder a surface wears until its first paint, never a verdict
 * while a CURRENT adoption runs. A boundary that cannot settle within this deadline refuses by name. */
type AdoptionRecord = { facet: 'entity' | 'inventory'; entity: string; base_rev: number | undefined };
const adoptions = new Map<string, Map<Promise<void>, AdoptionRecord>>();
const paintedEntities = new Set<string>();
const reportedEntityRefusals = new Map<string, { revision: number; reasons: Set<string> }>();
function reportEntityRefusal(id: string, reason: string, announce = true): void {
    if (!announce) {
        const key = id.toUpperCase();
        let revision = appliedRevisions.get(key) ?? 0;
        for (const envelope of entityRuntime.held.values()) {
            if (envelope.entity_id.toUpperCase() === key) revision = Math.max(revision, envelope.base_rev);
            for (const member of Array.isArray(envelope.base.encounter_creatures_arr) ? envelope.base.encounter_creatures_arr : [])
                if (fromBaseRecord(member) && String(member.entity_id).toUpperCase() === key)
                    revision = Math.max(revision, Number(member.base_rev ?? 0));
        }
        let previous = reportedEntityRefusals.get(key);
        if (previous?.revision !== revision) {
            previous = { revision, reasons: new Set() };
            reportedEntityRefusals.set(key, previous);
        }
        if (previous.reasons.has(reason)) return;
        previous.reasons.add(reason);
        while (reportedEntityRefusals.size > 4096) reportedEntityRefusals.delete(reportedEntityRefusals.keys().next().value!);
    }
    refuseUnported(reason, { entityId: id, ...missingInputDetail(id, reason) });
}
const PENDING_PLACEHOLDER = /^unported:[a-z]+-base-pending$/;
const pendingBirths = new Map<string, { encounterId: string; promise: Promise<void>; resolve(): void; releaseSave(): void }>();
/** hot2-fix1's readiness budget. An IDLE backstop armed afresh on every sign of progress, never an elapsed
 * deadline: a slow cold compile that keeps progressing is valid work; only a surface nothing touched for the
 * whole budget is refused by name. */
const ADOPTION_STALL_MS = 180_000;
/** Saved-base revisions already installed by this browser, independent of a lagging echo facet. */
const appliedRevisions = new Map<string, number>();

function observeAppliedRevisions(wire: string, held: Iterable<BaseEnvelope>, decoded?: AppliedFrameResult): () => void {
    const frame = decoded ?? parsedAppliedFrame(wire);
    if ('cache_ref' in frame) return () => {};
    const wanted = new Set(frame.rows.filter(row => row.table === 'encounter_creatures').map(row => `encounter_creatures:${row.row_id.toUpperCase()}`));
    const snapshots = [...held].map(envelope => {
        const base = envelope.base;
        const roster = Array.isArray(base.encounter_creatures_arr) ? base.encounter_creatures_arr : [];
        const members = [base, ...[...appliedListSlots(roster, wanted, true)].map(slot => roster[slot])];
        let contiguous = true;
        const revisions = members.flatMap(member => {
            const id = String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
            const rev = frame.member_revs[id];
            // Unchanged miss targets do not consume a saved-base revision. Never
            // compare a movement counter with a saved-base revision requirement.
            const before = Number(encounterCreatureField(member.encounter_creatures, 'movement_rev') ?? 0);
            if (!id || !Number.isSafeInteger(rev) || rev! <= before
                || !frame.rows.some(row => row.table === 'encounter_creatures' && row.row_id.toUpperCase() === id)) return [];
            if (rev !== before + 1 && frame.member_before_revs?.[id] !== before) { contiguous = false; return []; }
            return [[id, Math.max(appliedRevisions.get(id) ?? -1, Number(member.base_rev ?? 0)) + 1] as const];
        });
        return { envelope, base, revisions, contiguous };
    });
    return () => {
        let changed = false;
        for (const { envelope, base, revisions, contiguous } of snapshots) {
            // Duplicate, unavailable and failed installs leave the saved input
            // untouched; hearing a frame alone never establishes coverage.
            if (!contiguous || envelope.base === base) continue;
            for (const [id, revision] of [[envelope.entity_id.toUpperCase(), envelope.base_rev] as const, ...revisions]) {
                if (revision <= (appliedRevisions.get(id) ?? -1)) continue;
                appliedRevisions.set(id, revision); changed = true;
            }
        }
        if (changed) notifyAdoptionChange();
    };
}

function inFlight(id: string): boolean { return pendingFor([id.toUpperCase()]).length > 0; }
/** The encounters whose roster carries this member; their adoption paints the member's surface too. */
function owningEncounters(id: string): string[] {
    const key = id.toUpperCase(), owners: string[] = [];
    for (const encounter of window.FateTide?.encounterCache?.values() ?? []) {
        if (!(encounter.encounter_creatures_arr ?? []).some(member => String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id')).toUpperCase() === key)) continue;
        const owner = String((encounter as Row)._entity_base && fromBaseRecord((encounter as Row)._entity_base)
            ? ((encounter as Row)._entity_base as Row).entity_id : encounterField(encounter, 'encounter_id') ?? '').toUpperCase();
        if (owner) owners.push(owner);
    }
    return owners;
}
/** The adoptions and compiles that still answer for the revision each runtime holds now. An adoption of an
 * older revision is obsolete: the newer paint already covers its host, and its unanswered worker holds no boundary. */
function pendingFor(keys: readonly string[]): Promise<void>[] {
    return keys.flatMap(key => {
        const current = [...(adoptions.get(key) ?? [])].filter(([, record]) => {
            const held = (record.facet === 'entity' ? entityRuntime : runtime).get(key, record.entity);
            return record.base_rev === undefined || held === undefined || record.base_rev >= held.base_rev;
        }).map(([promise]) => promise);
        return [...current, pendingBirths.get(key)?.promise, entityRuntime.settled(key), runtime.settled(key), entityRuntime.recovering(key), runtime.recovering(key)]
            .filter((wait): wait is Promise<void> => wait !== undefined);
    });
}
function deferred(): { promise: Promise<void>; resolve(): void } {
    let resolve!: () => void;
    const promise = new Promise<void>(settle => { resolve = settle; });
    return { promise, resolve };
}
/** Every change of ownership or paint wakes the boundaries: an adoption registered or finished, a surface
 * painted or refused. A waiting boundary re-selects its pending set on each, so a surface that becomes
 * obsolete WHILE it waits releases it, and progress re-arms the idle backstop. */
let adoptionChanged = deferred();
function notifyAdoptionChange(): void {
    const previous = adoptionChanged;
    adoptionChanged = deferred();
    previous.resolve();
}
window.addEventListener('ft:spawn-preview-applied', event => {
    const detail = (event as CustomEvent).detail;
    const encounterId = String(detail?.encounter_id ?? '').toUpperCase();
    for (const id of detail?.creatureIds ?? []) {
        const key = String(id).toUpperCase();
        if (!pendingBirths.has(key)) pendingBirths.set(key, { encounterId, ...deferred(), releaseSave: beginSpawnSave(encounterId, [key]) });
    }
    notifyAdoptionChange();
});
function releaseBirth(key: string): void {
    const birth = pendingBirths.get(key);
    if (!birth) return;
    pendingBirths.delete(key); birth.resolve(); notifyAdoptionChange();
}
window.addEventListener('ft:spawn-save-settled', event => {
    const detail = (event as CustomEvent).detail;
    // A fast successful save can precede the worker: first paint still owns readiness.
    if (detail?.saved) return;
    for (const id of detail?.actorIds ?? []) releaseBirth(String(id).toUpperCase());
});
window.addEventListener('ft:entity-base-compiled', event => {
    if (!pendingBirths.size && !window.ftFailedSpawnSaves?.size) return;
    const detail = (event as CustomEvent).detail, computed = detail?.computed;
    if (!fromBaseRecord(computed)) return;
    const entityId = String(detail.entityId ?? '').toUpperCase();
    const members: Row[] = Array.isArray(computed.encounter_creatures_arr) ? computed.encounter_creatures_arr : [computed];
    const ids = members.map(row => String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase());
    if (!detail.applied) {
        const encounterId = Array.isArray(computed.encounter_creatures_arr) ? entityId
            : String(encounterCreatureField(computed.encounter_creatures, 'encounter_id') ?? '').toUpperCase();
        if (encounterId) reconcileSpawnSaves(encounterId, ids);
    }
    for (const [key, birth] of pendingBirths) {
        if (birth.encounterId !== entityId && key !== entityId) continue;
        const row = members[ids.indexOf(key)];
        if (row && PENDING_PLACEHOLDER.test(String(row._entity_refusal ?? ''))) continue;
        releaseBirth(key);
        if (!row) birth.releaseSave(); // The source/planner cleared this exact preview before any save.
    }
});
/** Only a first paint or an explicit revision gap holds an action. Background adoption of
 * an already painted surface never delays the next local decision. */
function actionNeedsBase(key: string): boolean {
    if (pendingBirths.has(key)) return true;
    if (entityRuntime.gap(key) !== undefined || runtime.gap(key) !== undefined) return true;
    if (paintedEntities.has(key)) return false;
    const hosts = [...(entitySurfaces.get(key) ?? []), ...(surfaces.get(key) ?? [])];
    for (const cache of [window.FateTide?.casterCache, window.FateTide?.encounterCache, window.FateTide?.objectCache]) {
        const host = cache?.get(key);
        if (fromBaseRecord(host)) hosts.push(host);
    }
    return hosts.some(host => [host._entity_refusal, host._inventory_refusal].some(reason => PENDING_PLACEHOLDER.test(String(reason ?? ''))));
}
function awaitEntityBase(ids: readonly string[]): Promise<boolean> | undefined {
    if (runtime.paused || entityRuntime.paused) return Promise.resolve(false);
    const keys = [...new Set(ids.flatMap(id => [id.toUpperCase(), ...owningEncounters(id)]))];
    const blocking = (): string[] => keys.filter(actionNeedsBase);
    for (const key of blocking()) { void entityRuntime.recover(key); void runtime.recover(key); }
    let waits = pendingFor(blocking());
    if (!waits.length) return undefined;
    return (async () => {
        while (waits.length) {
            let timer: ReturnType<typeof setTimeout> | undefined;
            const stalled = await Promise.race([
                ...waits.map(wait => wait.then(() => false, () => false)),
                adoptionChanged.promise.then(() => false),
                new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(true), ADOPTION_STALL_MS); }),
            ]);
            clearTimeout(timer);
            if (runtime.paused || entityRuntime.paused) return false;
            waits = pendingFor(blocking());
            if (stalled && waits.length) {
                refuseUnported('unported:entity-base:adoption-timeout', { entityId: blocking()[0], ids: keys, idleMs: ADOPTION_STALL_MS });
                return false;
            }
        }
        return true;
    })();
}

/** Encounter compilation owns member inventories without a second member compile or envelope copy. */
function releaseMemberInventories(owner: Row): void {
    for (const [member, id] of memberInventories.get(owner) ?? []) {
        const owners = memberInventoryOwners.get(member);
        owners?.delete(owner);
        if (!owners?.size && !adoptedSurfaces.has(member)) {
            const collection = surfaces.get(id);
            collection?.delete(member);
            if (!collection?.size) surfaces.delete(id);
        }
    }
    memberInventories.delete(owner);
}

function holdMemberInventories(owner: Row): void {
    const members = new Map<Row, string>();
    for (const member of Array.isArray(owner.encounter_creatures_arr) ? owner.encounter_creatures_arr : []) {
        if (!fromBaseRecord(member)) continue;
        const id = String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
        if (!id) continue;
        const collection = surfaces.get(id) ?? new Set<Row>();
        collection.add(member); surfaces.set(id, collection);
        const owners = memberInventoryOwners.get(member) ?? new Set<Row>();
        owners.add(owner); memberInventoryOwners.set(member, owners); members.set(member, id);
        member._inventory_refusal = member._entity_refusal
            ?? (member._inventory_refusal === 'unported:creature-base-pending' ? null : member._inventory_refusal) ?? null;
    }
    memberInventories.set(owner, members);
}

function catalogIsStale(version: string, ref: string): boolean {
    if (expiredCatalogVersions.has(version)) return true;
    const split = ref.lastIndexOf(':');
    if (split < 0) return false; // A globals-only read has no saved-row revision.
    const scope = ref.slice(0, split), id = scope.slice(scope.indexOf('~') + 1).toUpperCase();
    return Number(ref.slice(split + 1)) < Math.max(catalogRevisions.get(`${version}:${scope}`)?.revision ?? -1,
        heardBaseRevision(id) ?? -1, appliedRevisions.get(id) ?? -1, touchedMinimumRevisions.get(id) ?? -1);
}

function requestCatalog(version: string, ref: string): Promise<InventorySharedCatalog> {
    if (expiredCatalogVersions.has(version)) return Promise.reject(new Error('inventory-catalog:409'));
    const key = `${version}:${ref}`, previous = catalogs.get(key);
    if (previous) return previous;
    if (catalogIsStale(version, ref)) return Promise.reject(new Error('inventory-catalog:409'));
    const pending = new Promise<InventorySharedCatalog>((resolve, reject) => {
        let queue = catalogQueue.get(version);
        if (!queue) {
            queue = new Map(); catalogQueue.set(version, queue);
            setTimeout(() => { void flushCatalogs(version); }, 0);
        }
        queue.set(ref, { resolve, reject });
    });
    catalogs.set(key, pending);
    trackCatalogRevision(version, ref, pending);
    return pending;
}

function trackCatalogRevision(version: string, ref: string, pending: Promise<InventorySharedCatalog>): void {
    const key = `${version}:${ref}`;
    // Settled historical revisions pin whole catalogs; retain the current saved-row scope for each entity.
    // Keep pending scopes joinable until they settle so a newer read cannot orphan an older waiter.
    const split = ref.lastIndexOf(':'), revision = Number(ref.slice(split + 1));
    if (split >= 0 && Number.isSafeInteger(revision) && revision >= 0) {
        const owner = `${version}:${ref.slice(0, split)}`, current = catalogRevisions.get(owner);
        const retire = (oldKey: string, old: Promise<InventorySharedCatalog>): void => {
            if (catalogs.get(oldKey) === old) catalogs.delete(oldKey);
        };
        if (!current || revision > current.revision) {
            catalogRevisions.set(owner, { revision, key });
            const old = current && catalogs.get(current.key);
            if (old) void old.then(() => retire(current!.key, old), () => {});
        }
        void pending.then(() => {
            if (catalogRevisions.get(owner)?.key !== key) retire(key, pending);
        }, () => retire(key, pending));
    }
}

async function flushCatalogs(version: string): Promise<void> {
    const queue = catalogQueue.get(version);
    if (!queue) return;
    catalogQueue.delete(version);
    const entries = [...queue];
    for (let start = 0; start < entries.length; start += 64) {
        await readCatalogBatch(version, entries.slice(start, start + 64));
    }
}

async function readCatalogBatch(version: string, batch: [string, CatalogWaiter][]): Promise<void> {
    try {
        // A save/echo or newer SELECT can overtake the zero-delay batching window.
        const stale = batch.filter(([ref]) => catalogIsStale(version, ref));
        rejectCatalogs(version, stale, new Error('inventory-catalog:409'));
        batch = batch.filter(entry => !stale.includes(entry));
        if (!batch.length) return;
        const globals = globalCatalogs.has(version) ? '0' : '1';
        const route = version.startsWith('entity-') ? 'base_entity_catalog_inc' : 'base_inventory_catalog_inc';
        const response = await fetchSavedResponse(`${BASE_URL}options/api/${route}.php?version=${encodeURIComponent(version)}`
            + `&globals=${globals}&conflicts=resync&refs=${encodeURIComponent(batch.map(([ref]) => ref).join(','))}`);
        const value: unknown = response.status === 409 ? await response.json().catch(() => undefined)
            : response.ok ? await readSavedResponse(response) : undefined;
        if (response.status === 409 || fromBaseRecord(value)
            && (value.error === 'catalog-version-changed' || value.error === 'catalog-revision-changed')) {
            const error = await catalogReadError({ status: 409, json: async () => value }, version, batch.map(([ref]) => ref));
            if (fromBaseRecord(value) && value.error === 'catalog-version-changed') expiredCatalogVersions.add(version);
            // The comparison already identifies the changed scope. Never bisect
            // an epoch conflict into requests that all carry the obsolete version.
            const changed = fromBaseRecord(value) ? batch.filter(([ref]) => ref === value.requested_ref) : [];
            if (!changed.length) throw error;
            if (fromBaseRecord(value) && typeof value.current_revision === 'number' && Number.isSafeInteger(value.current_revision)) {
                const ref = changed[0]![0], id = ref.slice(ref.indexOf('~') + 1, ref.lastIndexOf(':')).toUpperCase();
                if (value.current_revision > (heardRevisions.get(id) ?? -1)) heardRevisions.set(id, value.current_revision);
            }
            rejectCatalogs(version, changed, error);
            await readCatalogBatch(version, batch.filter(entry => !changed.includes(entry)));
            return;
        }
        if (!response.ok) throw await catalogReadError(response, version, batch.map(([ref]) => ref));
        if (!fromBaseRecord(value) || value.version !== version
            || !fromBaseRecord(value.scopes) && !(batch.every(([ref]) => ref === '') && Array.isArray(value.scopes) && !value.scopes.length))
            throw new Error('inventory-catalog-shape');
        const scopes = fromBaseRecord(value.scopes) ? value.scopes : {};
        if (globals === '1') {
            if (!fromBaseRecord(value.base) || !fromBaseRecord(value.metadata)) throw new Error('inventory-catalog-shape');
            globalCatalogs.set(version, value as unknown as InventorySharedCatalog);
        }
        const shared = globalCatalogs.get(version)!;
        if (!fromBaseRecord(value.definitions)) throw new Error('inventory-catalog-definitions');
        const catalog = definitions.get(version) ?? {};
        Object.assign(catalog, value.definitions); definitions.set(version, catalog);
        for (const [ref, waiter] of batch) {
            if (ref === '') { waiter.resolve(shared); continue; }
            const scope = scopes[ref];
            if (!fromBaseRecord(scope)) throw new Error('inventory-catalog-scope');
            if (!Array.isArray(scope.catalog_ids)) throw new Error('inventory-catalog-ids');
            const selected: Row = {};
            for (const id of scope.catalog_ids) {
                if (typeof id !== 'string' || !fromBaseRecord(catalog[id])) throw new Error('inventory-catalog-definition');
                selected[id] = catalog[id];
            }
            const inputs = inventoryCatalogScope(shared, { ...scope, catalog: selected });
            for (const row of Object.values(selected)) if (fromBaseRecord(row)) definitionInputs.set(row, inputs.base);
            waiter.resolve(inputs);
        }
    } catch (error) {
        rejectCatalogs(version, batch, error);
    }
}

function rejectCatalogs(version: string, batch: [string, CatalogWaiter][], error: unknown): void {
    for (const [ref, waiter] of batch) { catalogs.delete(`${version}:${ref}`); waiter.reject(error); }
}

/** The server keeps the selected ids and sends only definitions absent from this epoch. */
function restoreInlineCatalog(envelope: BaseEnvelope): void {
    const scope = envelope.catalog_scope;
    if (!scope || !Array.isArray(scope.catalog_ids)) return;
    const held = definitions.get(envelope.catalog_version!);
    const incoming = fromBaseRecord(scope.catalog) ? scope.catalog : {};
    const selected: Row = {};
    for (const id of scope.catalog_ids) {
        if (typeof id !== 'string') throw new Error('inventory-catalog-definition');
        const row = incoming[id] ?? held?.[id];
        if (!fromBaseRecord(row)) throw new Error('inventory-catalog-definition');
        selected[id] = row;
    }
    scope.catalog = selected;
}

function rememberInlineCatalog(envelope: BaseEnvelope, inputs: InventorySharedCatalog): void {
    const scope = envelope.catalog_scope;
    if (!scope || !fromBaseRecord(scope.catalog)) return;
    const version = envelope.catalog_version!, ref = String(envelope.base.catalog_ref);
    const catalog = definitions.get(version) ?? {};
    Object.assign(catalog, scope.catalog); definitions.set(version, catalog);
    for (const row of Object.values(scope.catalog)) if (fromBaseRecord(row)) definitionInputs.set(row, inputs.base);
    const pending = Promise.resolve(inputs);
    catalogs.set(`${version}:${ref}`, pending);
    trackCatalogRevision(version, ref, pending);
}

function heldEntityCatalogHints(version: string): { known_catalog_version: string; known_catalog_ids: string[] } | undefined {
    // Embedded lean members need not retain their standalone envelope. The SELECT
    // still checks this epoch before omitting any definition from its response.
    if (!version) for (const current of globalCatalogs.keys())
        if (current.startsWith('entity-') && !expiredCatalogVersions.has(current)) version = current;
    return globalCatalogs.has(version) && !expiredCatalogVersions.has(version) && definitions.has(version)
        ? { known_catalog_version: version, known_catalog_ids: Object.keys(definitions.get(version)!) } : undefined;
}

async function hydrateCatalog(envelope: BaseEnvelope | BaseBusy): Promise<BaseEnvelope | BaseBusy> {
    if ('resync' in envelope || !envelope.catalog_version) return envelope;
    const ref = envelope.base.catalog_ref;
    // Applied births inherit their parent's version/metadata and carry their own
    // complete inline definition closure, without a standalone SELECT scope ref.
    // They need no hydration or recovery read, including with an empty inventory.
    if (typeof ref !== 'string' && envelope.metadata && fromBaseRecord(envelope.base.catalog)) {
        const catalog = envelope.base.catalog;
        if (!Array.isArray(envelope.base.inventory) || envelope.base.inventory.some(row => {
            const id = readInventoryField(row, 'inventory_item_id');
            return typeof id !== 'string' || !fromBaseRecord(catalog[id]);
        })) throw new Error('inventory-catalog-definition');
        return envelope;
    }
    if (typeof ref !== 'string') throw new Error('inventory-catalog-reference');
    restoreInlineCatalog(envelope);
    if (envelope.catalog_scope && catalogIsStale(envelope.catalog_version, ref)) throw new Error('inventory-catalog:409');
    // Fresh caster responses already selected their exact scope. Keep the normal
    // hydration/link/cache path, fetching only globals when this page is cold.
    const catalog = envelope.catalog_scope
        ? inventoryCatalogScope(globalCatalogs.get(envelope.catalog_version) ?? await requestCatalog(envelope.catalog_version, ''), envelope.catalog_scope)
        : await requestCatalog(envelope.catalog_version, ref);
    await yieldBetweenMemberCompiles();
    const full = attachInventoryCatalog(envelope, catalog);
    rememberInlineCatalog(envelope, catalog);
    delete full.catalog_scope;
    savedBases.set(full.base, savedBase(envelope));
    entityCompiler.link(envelope, full);
    return full;
}

/** Idle diagnostics borrow an already-requested scope. An unavailable closure
 * stays sparse; collecting evidence must never start another gameplay read. */
async function refusalSnapshotInputs(sources: { actor?: unknown; encounter?: unknown }) {
    const input = async (value: unknown): Promise<Row> => {
        const out: Row = {};
        if (!fromBaseRecord(value)) return out;
        await Promise.all(['_entity_base', '_inventory_base', '_inventory_held'].map(async key => {
            const envelope = value[key] as BaseEnvelope | BaseBusy | undefined;
            if (!envelope) return;
            out[key] = envelope;
            if (!('resync' in envelope) && (!envelope.metadata || !envelope.base.catalog) && envelope.catalog_version)
                try {
                    const catalog = await catalogs.get(`${envelope.catalog_version}:${envelope.base.catalog_ref}`);
                    if (catalog) out[key] = attachInventoryCatalog(envelope, catalog);
                } catch { /* Preserve the exact held ref when its existing read failed. */ }
        }));
        return out;
    };
    const [actor, encounter] = await Promise.all([input(sources.actor), input(sources.encounter)]);
    return { actor, encounter };
}

async function loadBase(id: string, entity: string): Promise<BaseEnvelope | BaseBusy> {
    const response = await fetchSavedResponse(`${BASE_URL}options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=${encodeURIComponent(entity)}&cold=0`);
    if (!response.ok) throw new Error(`base-read:${response.status}`);
    return readSavedResponse(response) as Promise<BaseEnvelope | BaseBusy>;
}

function withCatalog(envelope: BaseEnvelope | BaseBusy): Promise<BaseEnvelope | BaseBusy> {
    const previous = hydrationJobs.get(envelope);
    if (previous) return previous;
    const job = recoverBaseCatalog(envelope, hydrateCatalog, loadBase).catch(error => { hydrationJobs.delete(envelope); throw error; });
    hydrationJobs.set(envelope, job); return job;
}

async function preloadCatalogs(host: Row): Promise<Map<BaseEnvelope | BaseBusy, BaseEnvelope | BaseBusy>> {
    const jobs = new Map<BaseEnvelope | BaseBusy, Promise<BaseEnvelope | BaseBusy>>();
    const collect = (node: Row): void => {
        for (const key of ['_inventory_base', '_entity_base']) {
            const envelope = node[key] as BaseEnvelope | BaseBusy | undefined;
            if (envelope && !jobs.has(envelope)) {
                // A warm computed hit still needs its saved catalog before the first ordinary patch.
                const held = !('resync' in envelope) && envelope.metadata && fromBaseRecord(envelope.base.catalog);
                jobs.set(envelope, held || adoptedSurfaces.get(node) === envelope ? Promise.resolve(envelope) : withCatalog(envelope));
            }
        }
        for (const name of ['encounter_creatures_arr', 'location_encounter'])
            if (Array.isArray(node[name])) for (const child of node[name]) if (fromBaseRecord(child)) collect(child);
    };
    collect(host);
    return new Map(await Promise.all([...jobs].map(async ([envelope, job]) => [envelope, await job] as const)));
}

/** Batch sibling SELECT catalogs before the caller bounds native adoptions. */
async function preloadEntityCatalogs(hosts: readonly unknown[]): Promise<void> {
    await Promise.allSettled(hosts.filter(fromBaseRecord).map(host => preloadCatalogs(host)));
}

function versionChanged(): void {
    noteEngineReload();
    if (document.getElementById('ft-base-version-change')) return;
    const banner = document.createElement('div');
    banner.id = 'ft-base-version-change'; banner.setAttribute('role', 'alert');
    banner.className = 'alert alert-warning position-fixed w-100'; banner.style.zIndex = '2147483000';
    // Pinned: position-fixed with no offset keeps its static spot after the last child, below the fold (playtest 2026-10-13).
    banner.style.top = '0'; banner.style.left = '0';
    banner.textContent = ENGINE_RELOAD_MESSAGE;
    const button = document.createElement('button'); button.textContent = 'Reload'; button.className = 'btn btn-primary ml-2';
    button.addEventListener('click', () => window.location.reload()); banner.append(button);
    (document.body ?? document.documentElement).append(banner);
}

function hasHeldEncounter(): boolean {
    const held = (value: unknown): boolean => fromBaseRecord(value) && Array.isArray(value.encounter_creatures_arr);
    return held(window.FateTide?.encounterArr)
        || [...window.FateTide?.encounterCache?.values() ?? []].some(held);
}
const handleWorkerVersionChange = createCompileVersionRecovery(engine, {
    pause: next => { runtime.checkEngine(next); entityRuntime.checkEngine(next); notifyAdoptionChange(); },
    banner: versionChanged,
    hasEncounter: hasHeldEncounter,
    report: next => window.dispatchEvent(new CustomEvent('ft:unported', { detail: { mode: 'warning',
        reason: 'compile-worker-engine-mismatch', detail: { engine, workerEngine: next } } })),
    reload: () => window.location.reload(),
});
window.ftHandleCompileEngineMismatch = (error, next) => {
    if (!isCompileEngineMismatch(error)) return false;
    handleWorkerVersionChange(next ?? lastWorkerEngine); return true;
};

/** Overlay only display nodes. Runtime output and Redis write-back stay viewer-independent. */
function inventoryForViewer(computed: unknown, permissions: BaseEnvelope['viewer_permissions']): unknown {
    if (!permissions) return computed; // An older envelope retains its existing display contract.
    const stamp = (node: unknown): unknown => !fromBaseRecord(node) ? node : { ...node, permissions: { ...permissions },
        ...('inventory_in_container' in node
            ? { inventory_in_container: inventoryForViewer(node.inventory_in_container, permissions) } : {}) };
    return Array.isArray(computed) ? computed.map(stamp) : fromBaseRecord(computed)
        ? Object.fromEntries(Object.entries(computed).map(([key, node]) => [key, stamp(node)])) : computed;
}

function repaint(envelope: BaseEnvelope, computed: unknown): void {
    const id = envelope.entity_id.toUpperCase();
    paintedEntities.add(id);
    const display = inventoryForViewer(computed, envelope.viewer_permissions);
    for (const host of surfaces.get(id) ?? []) {
        if (fromBaseRecord(host.inventory)) {
            host._inventory_refusal = 'unported:inventory-instance-root';
            continue;
        }
        host.inventory = display;
        if (Array.isArray(display)) prepareInventoryDisplay(host, display as Row[], () => null);
        if ('encounter_inventories_arr' in host || fromBaseRecord(host.encounters)) host.encounter_inventories_arr = display;
        host._inventory_refusal = null;
    }
    try { hostWindow.ftRepaintCasterFromCache?.(envelope.entity_id); } catch { /* this page may only mount a floor tree */ }
    window.dispatchEvent(new CustomEvent('ft:inventory-base-compiled', { detail: { entityId: id, entity: envelope.entity, baseRev: envelope.base_rev, computed: display } }));
    notifyAdoptionChange();
}

const cacheSizeSkips = new Set<string>();
const floorPositionPaints = new WeakMap<BaseEnvelope, Row[]>();
const cacheWrites = createIdleCacheWriter(2000, () => Promise.allSettled(pendingFor(
    [...new Set([...runtime.held.values(), ...entityRuntime.held.values()].map(held => held.entity_id.toUpperCase()))],
)));
const touchedMinimumRevisions = new Map<string, number>();
function savedBase(envelope: BaseEnvelope): BaseEnvelope['base'] { return savedBases.get(envelope.base) ?? envelope.base; }
/** A raw vitality writer has no resource-truth frame to notify aura recipients. */
function announceVitalityRows(envelope: BaseEnvelope, frame: AppliedFrame): void {
    if (!frame.operation_id.startsWith('inventory-vitality:')) return;
    const creatureIds = frame.rows.filter(row => row.table === 'encounter_creatures').map(row => row.row_id.toUpperCase());
    if (!creatureIds.length) return;
    const encounters = envelope.entity === 'encounter' ? [envelope.entity_id.toUpperCase()] : creatureIds.flatMap(owningEncounters);
    for (const encounterId of new Set(encounters)) window.dispatchEvent(new CustomEvent('ft:encounter-rows-patched', {
        detail: { encounterId, creatureIds },
    }));
}
/** Applied frames repaint existing surfaces directly; preparePaint would clone a complete tree. */
function installPaint(envelope: BaseEnvelope, wire: string, decoded?: AppliedFrame): void {
    const id = envelope.entity_id.toUpperCase();
    const viewChanges = new Map<string, readonly string[] | undefined>();
    const hosts = new Set([...(entitySurfaces.get(id) ?? []), ...(surfaces.get(id) ?? [])]);
    let inventoryUnchanged = false;
    floorPositionPaints.delete(envelope);
    const incoming = decoded ?? parsedAppliedFrame(wire) as AppliedFrame;
    if (incoming.inputs?.scene_save) {
        for (const host of hosts) {
            for (const [member, paths] of appliedCasterViewChanges(incoming, host)) if (!viewChanges.has(member)) viewChanges.set(member, paths);
            const patched = applyAppliedFrame(incoming, host, false, false, undefined, false, envelope.metadata as unknown as import('../janus-engine/compile/from-base-encounter-terrain').EncounterTerrainMetadata);
            if (fromBaseRecord(patched.value) && patched.value !== host) paintAppliedTree(host, patched.value);
            Object.defineProperty(host, '_entity_base', { value: envelope, writable: true, configurable: true, enumerable: false });
            adoptedSurfaces.set(host, envelope);
        }
        const scene = incoming.inputs.scene_save as import('../janus-engine/transport/scene-save-frame').SceneSaveDelta;
        if (scene.lane === 'water_level') for (const wet of scene.data.wetRows as Array<{ actor_id: string }>)
            viewChanges.set(wet.actor_id.toUpperCase(), ['statuses.target', 'statuses.caster']);
        hostWindow.ftPatchAppliedCasterViews?.(viewChanges, id);
        // The renderer owns the one-component projection; no whole-floor/roster announcement.
        const targets = [window, ...Array.from(document.querySelectorAll<HTMLIFrameElement>('[data-ft3d-encounter-frame]')).flatMap(frame => frame.contentWindow ? [frame.contentWindow] : [])];
        for (const target of targets) (target as Window & { ft3dApplySceneSave?: (frame: AppliedFrame) => Promise<void> }).ft3dApplySceneSave?.(incoming)
            .catch(error => console.warn('[scene-save] narrow adoption failed', error));
        if ((incoming.inputs.scene_save as { lane?: string }).lane === 'room_music') window.dispatchEvent(new CustomEvent('ft:encounter-state-received', { detail: { encounterId: incoming.encounter_id } }));
        notifyAdoptionChange();
        return;
    }
    const mounted = floorEnchantmentOnlyRow(incoming);
    if (mounted) {
        for (const host of hosts) {
            const patched = patchFloorEnchantment(host, incoming, mounted, false) as Row;
            if (patched.encounter_inventories_arr) host.encounter_inventories_arr = patched.encounter_inventories_arr;
            else if (patched.inventory) host.inventory = patched.inventory;
            if (patched.catalog) host.catalog = patched.catalog;
            host._entity_refusal = null; host._inventory_refusal = null;
            if (envelope.entity !== 'inventory') {
                Object.defineProperty(host, '_entity_base', { value: envelope, writable: true, configurable: true, enumerable: false });
                adoptedSurfaces.set(host, envelope);
            }
        }
        floorPositionPaints.set(envelope, [mounted.node!]);
        announceEntityCompiled(envelope, [...hosts][0], true);
        floorPositionPaints.delete(envelope);
        notifyAdoptionChange();
        return;
    }
    // A shape-only saved row changes one shell field. Keep all resident member
    // objects and skip the broad applied-tree/view reconciliation entirely.
    const shapeRow = encounterShapeOnlyRow(incoming) ?? doorOpeningShapeRow(incoming, envelope.base);
    if (shapeRow) {
        const shapeHosts = hosts;
        for (const host of shapeHosts) {
            if (fromBaseRecord(host.encounters)
                && String(encounterField(host, 'encounter_id') ?? '').toUpperCase() === shapeRow.row_id.toUpperCase())
                host.encounters.encounter_shape = shapeRow.row.encounter_shape;
            if (envelope.entity !== 'inventory') {
                Object.defineProperty(host, '_entity_base', { value: envelope, writable: true, configurable: true, enumerable: false });
                adoptedSurfaces.set(host, envelope);
            }
            host._entity_refusal = null;
        }
        // Door truth has its own one-opening scene event after held adoption. Do not also
        // schedule the broad actor/resource projection over that same renderer.
        if (!incoming.inputs?.door_opening) announceEntityCompiled(envelope, [...shapeHosts][0], true, true);
        notifyAdoptionChange();
        return;
    }
    let controllers: string[] = [];
    const environmentRow = encounterEnvironmentOnlyRow(incoming);
    if (environmentRow) {
        if (!fromBaseRecord(envelope.base.encounters)) return;
        // The existing catalog adopter owns renderer/status consequences. Do not
        // repaint the encounter or announce a creature/inventory compilation.
        const field = Object.prototype.hasOwnProperty.call(environmentRow.row, 'encounter_light') ? 'encounter_light' : 'encounter_weather';
        const updates = { [field]: encounterField<string>(environmentRow.row, field) ?? null };
        void handleEncounterStatsUpdate({ type: 'encounter_stats_update', encounter_id: incoming.encounter_id, updates });
        for (const host of hosts) if (envelope.entity !== 'inventory') {
            Object.defineProperty(host, '_entity_base', { value: envelope, writable: true, configurable: true, enumerable: false });
            adoptedSurfaces.set(host, envelope);
        }
        notifyAdoptionChange();
        return;
    }
    try {
        inventoryUnchanged = appliedInventoryUnchanged(incoming, hosts);
        controllers = [...new Set(incoming.new_entities.map(entity => String(encounterCreatureField(entity.member.encounter_creatures,
            'controller_encounter_creature_id') ?? '').toUpperCase()).filter(Boolean))];
    } catch { /* unknown shape: reproject */ }
    for (const host of hosts) {
        const frame = incoming;
        try {
            for (const [member, paths] of appliedCasterViewChanges(frame, host)) if (!viewChanges.has(member)) viewChanges.set(member, paths);
        } catch {
            for (const member of Object.keys(frame.member_revs ?? {})) viewChanges.set(member, undefined);
        }
        const result = applyAppliedFrame(frame, host, false, false, undefined, false, envelope.metadata as unknown as import('../janus-engine/compile/from-base-encounter-terrain').EncounterTerrainMetadata);
        for (const member of result.recover) entityRuntime.recoverApplied(member, 'creature', frame.member_revs[member] ?? 0);
        for (const owner of result.inventoryRecover) runtime.recoverApplied(owner, 'inventory');
        if (!fromBaseRecord(result.value) || result.value === host) continue;
        releaseMemberInventories(host);
        paintAppliedTree(host, result.value);
        holdMemberInventories(host);
        if (envelope.entity !== 'inventory') {
            Object.defineProperty(host, '_entity_base', { value: envelope, writable: true, configurable: true, enumerable: false });
            adoptedSurfaces.set(host, envelope);
        }
        // The compiled birth consumes this marker to replace the raw member on every surface.
        if (host._entity_refusal !== 'unported:creature-base-pending') host._entity_refusal = null;
        if (Array.isArray(host.encounter_inventories_arr) && incoming.rows.every(row => row.table === 'inventory')
            && incoming.deletes.every(row => row.table === 'inventory') && !incoming.new_entities.length && !Object.keys(incoming.member_revs).length) {
            const floor = host.encounter_inventories_arr.filter(fromBaseRecord);
            const ids = new Set([...incoming.rows.filter(row => row.table === 'inventory').map(row => row.row_id.toUpperCase()),
                ...incoming.deletes.filter(row => row.table === 'inventory').map(row => row.row_id.toUpperCase())]);
            const footprints = Array.isArray(host.difficult_terrain_footprints) ? host.difficult_terrain_footprints.filter(fromBaseRecord) : [];
            if (ids.size && !incoming.new_inventory?.length) floorPositionPaints.set(envelope, [...ids].map(id => ({
                ...(floor.find(row => String(readInventoryField(row, 'inventory_id')).toUpperCase() === id)
                    ?? { inventory: { inventory_id: id, inventory_deleted: 1 } }),
                terrainFootprint: footprints.find(row => String(row.inventory_id).toUpperCase() === id) ?? null,
            })));
        }
        if (Array.isArray(host.encounter_inventories_arr) && incoming.operation_id.startsWith('saved-inventory-move:')) {
            // The moved subtree now belongs to its creature. Publish only its
            // former floor roots, even when the same frame carries AP/status rows.
            floorPositionPaints.set(envelope, (incoming.new_inventory ?? []).map(birth => ({
                inventory: { inventory_id: birth.entity_id, inventory_deleted: 1 }, terrainFootprint: null,
            })));
        }
    }
    projectNewbornControl(envelope, hosts, incoming.new_entities);
    const weightOwners = incoming.inputs?.inventory_weight_owners;
    if (Array.isArray(weightOwners) && weightOwners.length) {
        const changed = new Map<string, Row>();
        for (const host of hosts) for (const row of [host, ...(Array.isArray(host.encounter_creatures_arr) ? host.encounter_creatures_arr.filter(fromBaseRecord) : [])]) {
            const owner = String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
            if (weightOwners.includes(owner)) changed.set(owner, row);
        }
        for (const [owner, row] of changed) window.dispatchEvent(new CustomEvent('ft:inventory-weight-changed', { detail: { owner, row } }));
    }
    hostWindow.ftPatchAppliedCasterViews?.(viewChanges, id);
    announceEntityCompiled(envelope, [...hosts][0], true, inventoryUnchanged, controllers, incoming.operation_id);
    floorPositionPaints.delete(envelope);
    announceVitalityRows(envelope, incoming);
    notifyAdoptionChange();
}
const transportHooks: Pick<BaseRuntimeHooks, 'load' | 'installPaint' | 'recoverInventory' | 'writeBack' | 'canWriteBack' | 'diagnostics' | 'discard' | 'retainSnapshot' | 'copyComputed' | 'savedBase' | 'inventoryMoveContext'> = {
    inventoryMoveContext(envelope) {
        if (envelope.entity === 'encounter') return;
        const eid = String(encounterField(envelope.base.possessor, 'encounter_id')
            ?? encounterCreatureField(fromBaseRecord(envelope.base.possessor) ? envelope.base.possessor.encounter_creatures : null, 'encounter_id')
            ?? encounterField(envelope.base.encounter_context, 'encounter_id') ?? envelope.entity_id).toUpperCase();
        const encounter = entityRuntime.get(eid, 'encounter');
        const computed = window.FateTide?.encounterCache?.get(eid);
        return encounter && computed ? { envelope: encounter, computed } : undefined;
    },
    installPaint,
    recoverInventory: id => runtime.recoverApplied(id, 'inventory'),
    discard: entityCompiler.discard,
    retainSnapshot: entityCompiler.retainSnapshot,
    copyComputed: async value => mutableSurface(value),
    savedBase,
    canWriteBack(envelope) {
        const id = envelope.entity_id.toUpperCase();
        return envelope.base_rev >= Math.max(heardBaseRevision(id) ?? -1, touchedMinimumRevisions.get(id) ?? -1);
    },
    diagnostics(envelope, reasons) {
        for (const reason of reasons) skipUnported(reason, { entityId: envelope.entity_id, baseRev: envelope.base_rev, ...missingInputDetail(envelope.entity_id, reason) });
    },
    async load(id, entity) {
        const shared = await readParentComputed(id, entity);
        if (shared) return shared;
        const response = await fetchSavedResponse(`${BASE_URL}options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=${encodeURIComponent(entity)}&cold=0`);
        if (!response.ok) throw new Error(`base-read:${response.status}`);
        const envelope = await readSavedResponse(response) as BaseEnvelope | BaseBusy;
        // The SELECT confirms this exact version and saved-row revision. Its held
        // closure stays valid; changed refs use their own key and failed reads evict theirs.
        return withCatalog(envelope);
    },
    async writeBack(envelope, computed, diagnostics) {
        markStall('xz:cache-write');
        if (ftCurrentTruthSink() || !transportHooks.canWriteBack!(envelope)) return;
        const key = baseEntityKey(envelope.entity_id, envelope.entity);
        markStall(`xz:cache-write:enqueue:${key}`);
        const snapshot = computed && typeof computed === 'object' ? entityCompiler.snapshot(computed) : undefined;
        cacheWrites.enqueue(key, async () => {
            markStall(`xz:cache-write:drain:${key}`);
            const generation = cacheWrites.generation();
            const current = () => {
                const valid = generation === cacheWrites.generation() && transportHooks.canWriteBack!(envelope)
                    && (envelope.entity === 'inventory' ? runtime : entityRuntime).isCurrent(envelope);
                if (!valid) markStall(`xz:cache-write:obsolete:${key}`);
                return valid;
            };
            if (!current()) return;
            const bytes = snapshot ?? JSON.stringify({ facet: envelope.entity === 'inventory' ? 'inventory' : 'entity',
                entity: envelope.entity, entity_id: envelope.entity_id, base_rev: envelope.base_rev, engine, catalog_version: envelope.catalog_version ?? null,
                dependency_revs: envelope.base.dependency_revs ?? {}, dependency_revs_invalidate_only: envelope.base.dependency_revs_invalidate_only ?? {},
                computed: envelope.entity === 'inventory' ? computed : sharedEntityComputed(computed), stage_diagnostics: diagnostics ?? [] });
            await yieldForCacheWrite();
            if (!current()) return;
            markStall(`xz:cache-write:prepare:${key}`);
            const request = await entityCompiler.cacheBody(bytes, skip => {
                const key = `${envelope.entity}:${envelope.entity_id}:${skip.reason}`;
                if (cacheSizeSkips.has(key)) return;
                cacheSizeSkips.add(key);
                // Existing ft:unported telemetry only; no saved janus_unported_skip row or gameplay refusal.
                skipUnported(skip.reason, { entityId: envelope.entity_id, baseRev: envelope.base_rev, ...skip });
            });
            if (!request || !current()) return;
            markStall(`xz:cache-write:upload:${key}`);
            const response = await fetch(BASE_URL + 'options/api/computed_cache_put_inc.php', {
                method: 'POST', headers: request.headers, body: request.body as BodyInit,
            });
            markStall(`xz:cache-write:response:${response.status}:${key}`);
            const served = response.headers.get('X-FT-Engine');
            if (served) { runtime.checkEngine(served); entityRuntime.checkEngine(served); }
            if (response.status !== 204) throw new Error(`computed-cache-put:${response.status}`);
        });
    },
};

const runtime = createBaseRuntime(engine, {
    ...transportHooks,
    compile: compileEnvelope,
    appliedRevision: id => appliedRevisions.get(id),
    paint: repaint,
    refuse(id, stages) {
        if (stages.some(stage => window.ftHandleCompileEngineMismatch?.(stage))) return;
        const reason = `unported:inventory-base:${stages.join(',')}`;
        for (const host of surfaces.get(id.toUpperCase()) ?? []) {
            if (!fromBaseRecord(host.inventory)) host.inventory = null;
            if ('encounter_inventories_arr' in host || fromBaseRecord(host.encounters)) host.encounter_inventories_arr = null;
            host._inventory_refusal = reason;
        }
        try { hostWindow.ftRepaintCasterFromCache?.(id); } catch { /* no sheet mounted */ }
        window.dispatchEvent(new CustomEvent('ft:inventory-base-refused', { detail: { entityId: id, stages } }));
        notifyAdoptionChange();
    },
    versionChanged,
});

function announceEntityCompiled(envelope: BaseEnvelope, computed: unknown, applied = false, inventoryUnchanged = false, controllers: readonly string[] = [], operationId?: string): void {
    const id = envelope.entity_id.toUpperCase();
    if (!controllers.includes(id)) try { hostWindow.ftRepaintCasterFromCache?.(id); } catch { /* no mounted sheet */ }
    if (controllers.length) window.dispatchEvent(new CustomEvent('ft:summons-changed', { detail: { creatureIds: controllers } }));
    window.dispatchEvent(new CustomEvent('ft:entity-base-compiled', { detail: { entityId: id, entity: envelope.entity, baseRev: envelope.base_rev, computed, applied, operationId } }));
    // An applied action that touched no inventory says so: the encounter projection then moves actors only, not the whole floor.
    window.dispatchEvent(new CustomEvent('ft:inventory-base-compiled', { detail: { entityId: id, baseRev: envelope.base_rev,
        ...(floorPositionPaints.has(envelope) ? { inventoryRows: floorPositionPaints.get(envelope) } : {}), ...(inventoryUnchanged ? { inventoryUnchanged } : {}) } }));
}

const skillGrantWork = { operations: 0, pathCopies: 0, skillRows: 0, ms: 0 };
const skillGrantMetadata = new WeakMap<object, ReturnType<typeof entityRuntimeMetadata>>();
function paintSkillGrantPatch(envelope: BaseEnvelope, patch: SavedSkillGrantPatch): void {
    skillGrantWork.operations++; skillGrantWork.pathCopies += patch.work.pathCopies;
    skillGrantWork.skillRows += patch.work.skillRows; skillGrantWork.ms += patch.work.ms;
    const hosts = new Set<Row>(entitySurfaces.get(envelope.entity_id.toUpperCase()) ?? []);
    for (const cache of [window.FateTide?.casterCache, window.FateTide?.encounterCache, window.FateTide?.objectCache])
        for (const row of cache?.values() ?? []) if (fromBaseRecord(row)) hosts.add(row);
    const visited = new Set<Row>();
    for (const host of hosts) for (const member of [host, ...(Array.isArray(host.encounter_creatures_arr)
        ? host.encounter_creatures_arr.filter(fromBaseRecord) : [])]) {
        if (visited.has(member)) continue;
        visited.add(member);
        const id = String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
        const section = patch.sections.find(section => section.id === id);
        if (!section || !fromBaseRecord(member.computed) || !fromBaseRecord(member.computed.creature)) continue;
        const creature = member.computed.creature, skills = fromBaseRecord(creature.skills) ? creature.skills : (creature.skills = {});
        if (section.conditionLayers && Object.keys(section.conditionLayers).length)
            Object.assign(member.item_layer_types ??= {}, section.conditionLayers);
        const abilities = fromBaseRecord(creature.abilities) ? creature.abilities : (creature.abilities = {});
        for (const key of Object.keys(section.abilities)) abilities[key] = section.abilities[key];
        for (const [key, value] of Object.entries(section.dependentCreature ?? {})) {
            if (value !== undefined) creature[key] = value;
            else delete creature[key];
        }
        if (section.vitality !== undefined) member.computed.vitality = section.vitality;
        if (section.vitalityMaximum !== undefined && fromBaseRecord(member.encounter_creatures))
            member.encounter_creatures.v_max = section.vitalityMaximum;
        if (section.movementRevision !== undefined && fromBaseRecord(member.encounter_creatures))
            member.encounter_creatures.movement_rev = section.movementRevision;
        if (section.grants) creature.skill_tier_grants = section.grants; else delete creature.skill_tier_grants;
        for (const key of section.changedSkills) {
            if (section.skills[key]) skills[key] = section.skills[key]; else delete skills[key];
        }
        member.user_choices = section.choices;
        (window as Window & { ftPatchSkillGrantRows?: (id: string, variables: readonly string[]) => void })
            .ftPatchSkillGrantRows?.(id, section.changedSkills);
    }
}
// Both facets reuse C4's runtime and transport; this bundle owns their maps and publishes one bridge.
const entityRuntime = createBaseRuntime(engine, {
    installNewEntities: true,
    loadMembers: loadRelocationMembers,
    async compileNewEntities(entities, context, bankInputs) {
        // Birth rows are already held. A catalog-ref conflict after their save
        // must never reload that scope; only missing inputs need a keyed read.
        const input = await spawnCompileInput(entities, context, (ids, templates) => readSpawnDefinitions(ids, context.catalog_version, templates));
        const result = await entityCompiler.previewSpawn(input);
        // Bank only shared raw inputs; saved members and their compiled trees keep their identity.
        if (input.entities[0]) {
            const shared = encounterMemberSharedInputs(input.entities[0].base);
            delete shared.inventory_order;
            bankInputs(shared, input.metadata);
        }
        if (!Array.isArray(result.new_entities)) throw new Error('spawn:worker-birth-shape');
        return result.new_entities as import('../janus-engine/transport/applied-frame').AppliedNewEntity[];
    },
    async bankNewEntities(entities, context) {
        const reads = new Set<Promise<{ base: Row; metadata: unknown }>>();
        try {
            const input = await spawnCompileInput(entities, context, (ids, templates) => {
                const read = readSpawnDefinitions(ids, context.catalog_version, templates);
                reads.add(read); return read;
            });
            const base = encounterMemberSharedInputs(input.entities[0]?.base ?? {});
            delete base.inventory_order;
            return { base, metadata: input.metadata };
        } catch (error) {
            // A timed-out transport may ignore abort. Its parked cache entry must
            // not make a later birth join that same unavailable read forever.
            for (const [key, read] of spawnDefinitionReads) if (reads.has(read)) spawnDefinitionReads.delete(key);
            throw error;
        }
    },
    ...transportHooks,
    compile: compileEnvelope,
    async savedSkillGrantPatch(envelope, ops, computed) {
        if (!fromBaseRecord(envelope.metadata)) return null;
        let metadata = skillGrantMetadata.get(envelope.metadata);
        if (!metadata) {
            metadata = entityRuntimeMetadata(envelope.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
            skillGrantMetadata.set(envelope.metadata, metadata);
        }
        return savedSkillGrantPatch(envelope, ops, computed, metadata.player);
    },
    paintSkillGrantPatch,
    appliedRevision: id => appliedRevisions.get(id),
    observeApplied: observeAppliedRevisions,
    async preparePaint(envelope, computed) {
        const announcement = await prepareEntitySurface(envelope, computed);
        return { announcement };
    },
    paint(envelope, computed, prepared) {
        const id = envelope.entity_id.toUpperCase();
        paintedEntities.add(id);
        const announcement = prepared ? (computed as { announcement: unknown }).announcement : prepareCachedEntitySurface(envelope, computed);
        // A copied optimistic member can outlive its pending operation before this snapshot is banked.
        const savedMembers = (window.FateTide?.encounterCache?.get(id)?.encounter_creatures_arr ?? []).filter(row => {
            const memberId = String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '');
            if (isTruthCorrectionPending(id, memberId)) return false;
            if (isTruthCorrectionPending(id)) return true;
            const confirmed = hostWindow.ftMovementTruthConfirmed?.(id, memberId);
            return !confirmed || Number(encounterCreatureField(row.encounter_creatures, 'movement_rev') ?? 0) === confirmed.rev;
        });
        // Capture before any registered host paints: detached resync surfaces
        // borrow the same header as the newer canonical members they retain.
        const combatHeader = readCombatEncounterHeader(window.FateTide?.encounterCache?.get(id));
        for (const host of entitySurfaces.get(id) ?? []) {
            if (baseEntityKind((host._entity_base as BaseEnvelope | undefined)?.entity ?? '') !== baseEntityKind(envelope.entity)) continue;
            releaseMemberInventories(host);
            paintEntitySurface(host, envelope, announcement, savedMembers as Row[], true, combatHeader);
            Object.defineProperty(host, '_entity_base', { value: envelope, enumerable: false, writable: true, configurable: true });
            adoptedSurfaces.set(host, envelope);
            const collection = surfaces.get(id) ?? new Set<Row>(); collection.add(host); surfaces.set(id, collection);
            host._inventory_refusal = null;
            holdMemberInventories(host);
            if (envelope.entity === 'encounter') encounterPaintGenerations.set(host, encounterMutationGeneration(id));
        }
        announceEntityCompiled(envelope, announcement);
        notifyAdoptionChange();
    },
    refuse(id, stages, entity) {
        if (stages.some(stage => window.ftHandleCompileEngineMismatch?.(stage))) { notifyAdoptionChange(); return; }
        const reason = `unported:entity-base:${stages.join(',')}`;
        // A refused fetched candidate must not erase a previously mounted, compiled surface.
        const matches = (host: Row): boolean => !entity || baseEntityKind((host._entity_base as BaseEnvelope | undefined)?.entity ?? '') === baseEntityKind(entity);
        const adopting = [...adoptingEntitySurfaces.get(id.toUpperCase()) ?? []].filter(matches);
        // A raw newborn is held by its encounter before it has an entity
        // surface. Its first worker failure must replace the pending marker too.
        const births = [...surfaces.get(id.toUpperCase()) ?? []]
            .filter(host => host._entity_refusal === 'unported:creature-base-pending');
        const hosts = adopting.length ? adopting : [...entitySurfaces.get(id.toUpperCase()) ?? []].filter(matches);
        for (const host of new Set([...hosts, ...births])) {
            releaseMemberInventories(host);
            for (const key of Object.keys(host))
                if (!key.startsWith('_') && !['items', 'encounter_creatures', 'player_characters', 'encounters', 'locations', 'permissions', 'preview_context'].includes(key)) delete host[key];
            host._entity_refusal = reason; host._inventory_refusal = reason;
            if (stages.some(stage => stage.startsWith('base-read:'))) host.permissions = {};
        }
        if (births.length) releaseBirth(id.toUpperCase());
        window.dispatchEvent(new CustomEvent('ft:entity-base-refused', { detail: { entityId: id, reason, stages } }));
        reportEntityRefusal(id, reason, false);
        notifyAdoptionChange();
    },
    versionChanged,
});

/** Adoption holds the raw catalog for saved-row patches. Auxiliary unhydrated
 * envelopes retain the existing revision-fenced catalog recovery. */
async function compileEnvelope(envelope: BaseEnvelope) {
    if (!envelope.metadata && !envelope.catalog_version) return { computed: undefined, stagesRun: [], stagesMissing: ['frontend-metadata'] };
    const full = envelope.metadata ? envelope : await withCatalog({ ...envelope, cache: null });
    if ('resync' in full) throw new Error('compile-worker-revision-changed');
    // The catalog read found a commit had moved the base on: the runtime follows that fresher revision.
    if (full.base_rev > envelope.base_rev) throw new BaseRevisionSuperseded(full);
    if (full.base_rev !== envelope.base_rev) throw new Error('compile-worker-revision-changed');
    // Standalone member facets borrow the already-held encounter's raw connection/vehicle rows.
    // A status patch never needs an encounter read just to keep operating controls available.
    const peers = owningEncounters(full.entity_id).flatMap(id => {
        const source = entityRuntime.get(id, 'encounter')?.base;
        return source ? [source] : [];
    });
    if (peers.length && full.entity !== 'inventory' && !Array.isArray(full.base.encounter_creatures_arr))
        refreshStatusInputs(full.base, [], peers);
    let read = false;
    const base = await memberStatusInputs(full.base, id => {
        const raw = definitions.get(full.catalog_version ?? '')?.[id];
        if (fromBaseRecord(raw)) return definitionInputs.get(raw);
        const store = (window as unknown as { __ftDefinitionStore?: { peek?(id: string): { def: Row } | null } }).__ftDefinitionStore;
        for (const held of [window.FateTide?.objectCache?.get(id), store?.peek?.(id)?.def]) {
            const source = (held as Row | undefined)?._entity_base as BaseEnvelope | undefined;
            if (source?.engine === full.engine && source.catalog_version === full.catalog_version) return source.base;
        }
        // A definition save can advance the catalog epoch after this base was adopted.
        return read ? heldDefinitionInputs(id, full.engine) : undefined;
    }, ids => { read = true; return fetchMemberDefinitions(ids, full.catalog_version); });
    return entityCompiler.compile(base === full.base ? full : { ...full, base });
}

/** Practice owns a complete local closure: no recovery read or cache write-back. */
export function compileLocalTruthBase(envelope: BaseEnvelope) {
    return entityCompiler.compile({ ...envelope, cache: null });
}

type SharedBaseRequest = Pick<BaseEnvelope, 'entity' | 'entity_id' | 'engine'>;
async function sharedComputed(envelope: BaseEnvelope | SharedBaseRequest): Promise<(NonNullable<BaseEnvelope['cache']> & { envelope?: BaseEnvelope }) | undefined> {
    if (envelope.engine !== engine) return;
    await Promise.allSettled(pendingFor([envelope.entity_id.toUpperCase()]));
    const owner = envelope.entity === 'inventory' ? runtime : entityRuntime;
    const held = 'base' in envelope ? envelope : owner.get(envelope.entity_id, envelope.entity);
    if (!held) return;
    const cache = await owner.cached(held);
    if (!cache) return;
    if ('base' in envelope) return cache;
    // Each realm may patch its saved rows; neither the base nor computed tree crosses by reference.
    const copy = await copyTree({ ...held, base: savedBase(held), metadata: undefined, cache: null }) as BaseEnvelope;
    if (!owner.isCurrent(held)) return;
    return { ...cache, envelope: copy };
}
const sharedBaseBridge = Object.assign(sharedComputed, {
    owns(id: string, entity: string, requestedEngine = engine): boolean {
        const owner = entity === 'inventory' ? runtime : entityRuntime;
        return requestedEngine === engine && !owner.paused && owner.get(id, entity)?.engine === engine;
    },
});
declare global { interface Window { ftSharedComputedBase?: typeof sharedBaseBridge } }
window.ftSharedComputedBase = sharedBaseBridge;

/** Live owner gates update the same skill leaves in every held member memo. */
function bankAuraOwnerSkills(id: string, leaves: Row): number {
    let copies = 0;
    entityRuntime.patchMemberSections(id, (base, computed) => {
        copies += Object.values(leaves).filter(value => value !== undefined).length;
        return { base, computed: patchAuraOwnerSkillLeaves(computed, leaves) };
    });
    return copies;
}
window.ftBankAuraOwnerSkills = bankAuraOwnerSkills;

function parentComputedBridge(): typeof sharedBaseBridge | undefined {
    try { if (window.parent !== window) return window.parent.ftSharedComputedBase; } catch { /* Cross-origin embeds own their runtime. */ }
}
async function readParentComputed(id: string, entity: string): Promise<BaseEnvelope | undefined> {
    const read = parentComputedBridge();
    if (!read?.owns?.(id, entity, engine)) return;
    const shared = await read({ entity, entity_id: id, engine }).catch(() => undefined);
    if (!shared?.envelope) return;
    const { envelope, ...cache } = shared;
    return { ...envelope, cache };
}

// Embedded scenes follow the parent's completed paint, including resync-only echoes.
try {
    if (window.parent !== window) {
        const lifetime = new AbortController();
        window.addEventListener('pagehide', () => lifetime.abort(), { once: true });
        window.parent.addEventListener('ft:base-applied', event => {
            const wire = (event as CustomEvent<{ wire: import('../janus-engine/transport/applied-frame-plan').AppliedFrameInput }>).detail?.wire;
            if (wire && (typeof wire === 'string' || typeof wire === 'object')) void handleBasePatch({ type: 'base_patch', encounter_id: null, ops: [], applied: wire });
        }, { signal: lifetime.signal });
        for (const type of ['ft:entity-base-compiled', 'ft:inventory-base-compiled']) {
            window.parent.addEventListener(type, event => {
                const detail = (event as CustomEvent<{ entityId: string; entity?: string; computed?: unknown; applied?: boolean }>).detail;
                if (!detail?.entity || !('computed' in detail) || detail.applied) return;
                const owner = detail.entity === 'inventory' ? runtime : entityRuntime;
                if (!owner.get(detail.entityId, detail.entity)) return;
                void readParentComputed(detail.entityId, detail.entity).then(shared => shared && owner.adopt(shared, false)).catch(() => {});
            }, { signal: lifetime.signal });
        }
    }
} catch { /* Standalone or cross-origin scenes keep their own runtime. */ }

function localBaseFrame(owner: typeof runtime, frame: BasePatchFrame): BasePatchFrame {
    const parent = parentComputedBridge();
    if (!parent?.owns) return frame;
    const shared = new Set([...owner.held.values()].filter(held => parent.owns(held.entity_id, held.entity, engine)).map(held => held.entity_id.toUpperCase()));
    return { ...frame, ops: frame.ops.filter(op => !shared.has(op.entity_id.toUpperCase())), resync: frame.resync?.filter(id => !shared.has(id.toUpperCase())) };
}

async function shareParentComputed(host: Row): Promise<void> {
    let read: typeof sharedComputed | undefined;
    try { if (window.parent !== window) read = window.parent.ftSharedComputedBase; } catch { return; }
    if (!read) return;
    for (const name of ['_entity_base', '_inventory_base']) {
        const envelope = host[name] as BaseEnvelope | BaseBusy | undefined;
        if (!envelope || 'resync' in envelope) continue;
        const shared = await readParentComputed(envelope.entity_id, envelope.entity);
        const cache = shared?.cache ?? await read({ ...envelope, base: savedBase(envelope) }).catch(() => undefined);
        if (cache) Object.defineProperty(host, name, { value: shared ?? { ...envelope, cache }, enumerable: false, writable: true, configurable: true });
    }
}

/** An event races the observer's worker. No polling or delayed retry is needed. */
export async function handleComputedReady(frame: { entity: string; entity_id: string; base_rev: number; engine?: string; catalog_version?: string | null }): Promise<void> {
    if (frame.engine !== engine) return;
    const owner = frame.entity === 'inventory' ? runtime : entityRuntime;
    const held = owner.get(frame.entity_id, frame.entity);
    if (!held || held.base_rev !== frame.base_rev) return;
    if (parentComputedBridge()?.owns?.(frame.entity_id, frame.entity, engine)) return;
    const url = new URL('options/api/computed_cache_get_inc.php', new URL(BASE_URL, location.href));
    for (const [key, value] of Object.entries(frame)) if (value != null) url.searchParams.set(key, String(value));
    url.searchParams.set('entity', held.entity);
    try {
        const response = await fetchSavedResponse(url.href);
        if (!response.ok) return;
        const cache = await readSavedResponse(response) as BaseEnvelope['cache'];
        if (cache) owner.computedReady({ ...held, base_rev: frame.base_rev, cache });
    } catch { /* The local worker remains the competing source for this revision. */ }
}

/** One bundle owns state. Every other bundle reaches this bridge rather than importing it. */
/** Retire replaced canonical hosts after the new adopted row has actually entered the cache. */
function watchEncounterCacheRetirement(): void {
    const cache = window.FateTide?.encounterCache;
    if (!cache || watchedEncounterCaches.has(cache)) return;
    watchedEncounterCaches.add(cache);
    const set = cache.set;
    cache.set = function (key, next) {
        const previous = this.get(key), result = set.call(this, key, next);
        if (previous === next || !fromBaseRecord(next) || !adoptedSurfaces.has(next)
            || typeof next._entity_refusal === 'string') return result;
        // Optimistic copies keep their existing lifetime; a shared cache alias still owns its host.
        for (const value of this.values()) if (value === previous) return result;
        discardInventoryBase(previous);
        return result;
    };
}

export function adoptInventoryBase(host: unknown, options: { transient?: boolean; detached?: boolean } = {}): Promise<Row | void> {
    watchEncounterCacheRetirement();
    if (!fromBaseRecord(host)) return Promise.resolve();
    const previous = adoptionJobs.get(host);
    const input = { entity: host._entity_base, inventory: host._inventory_base, carried: host._inventory_held };
    if (previous && previous.entity === input.entity && previous.inventory === input.inventory && previous.carried === input.carried) return previous.promise;
    const owner: SurfaceAdoption = { ...input, promise: Promise.resolve() };
    let finishAdoption: () => void = () => {};
    const adoption = new Promise<void>(resolve => { finishAdoption = resolve; });
    const adoptionRecords: Array<[string, AdoptionRecord]> = [];
    for (const [key, facet] of [['_entity_base', 'entity'], ['_inventory_base', 'inventory'], ['_inventory_held', 'inventory']] as const) {
        const envelope = host[key];
        if (!fromBaseRecord(envelope) || typeof envelope.entity_id !== 'string') continue;
        adoptionRecords.push([envelope.entity_id.toUpperCase(), { facet, entity: String(envelope.entity), base_rev: typeof envelope.base_rev === 'number' ? envelope.base_rev : undefined }]);
    }
    for (const [id, record] of adoptionRecords) { const set = adoptions.get(id) ?? new Map<Promise<void>, AdoptionRecord>(); set.set(adoption, record); adoptions.set(id, set); }
    if (adoptionRecords.length) notifyAdoptionChange();
    const encounterId = fromBaseRecord(input.entity) && input.entity.entity === 'encounter' ? String(input.entity.entity_id) : '';
    const mutation = encounterReadGenerations.get(host) ?? encounterMutationGeneration(encounterId);
    const job = Promise.resolve().then(async () => {
        if (adoptionJobs.get(host) !== owner) return;
        await shareParentComputed(host);
        if (adoptionJobs.get(host) !== owner) return;
        // Parent reuse can finish behind this HTTP read. Keep the newer saved
        // envelope so its delivery retires any outstanding revision requirement.
        for (const [name, inputEnvelope] of [['_entity_base', input.entity], ['_inventory_base', input.inventory]] as const) {
            const incoming = inputEnvelope as BaseEnvelope | BaseBusy | undefined;
            const shared = host[name] as BaseEnvelope | BaseBusy | undefined;
            if (incoming && shared && !('resync' in incoming) && !('resync' in shared)
                && incoming.engine === shared.engine && baseEntityKey(incoming.entity_id, incoming.entity) === baseEntityKey(shared.entity_id, shared.entity)
                && incoming.base_rev > shared.base_rev)
                Object.defineProperty(host, name, { value: incoming, enumerable: false, writable: true, configurable: true });
        }
        owner.entity = host._entity_base; owner.inventory = host._inventory_base;
        return adoptSurface(host, options, mutation);
    }).finally(() => {
        if (adoptionJobs.get(host) === owner) adoptionJobs.delete(host);
        for (const [id] of adoptionRecords) {
            const set = adoptions.get(id);
            set?.delete(adoption);
            if (set && !set.size) adoptions.delete(id);
        }
        finishAdoption();
        if (adoptionRecords.length) notifyAdoptionChange();
    });
    owner.promise = job;
    adoptionJobs.set(host, owner);
    return job;
}
async function adoptSurface(host: Row, options: { transient?: boolean; detached?: boolean } = {}, startedMutation?: number): Promise<Row | void> {
    const owner = adoptionJobs.get(host);
    const transientMembers = options.transient && !memberInventories.has(host);
    const temporary: { id: string; collection: Set<Row>; registry: Map<string, Set<Row>> }[] = [];
    const register = (id: string, registry = surfaces): void => {
        const collection = registry.get(id) ?? new Set<Row>();
        if (options.transient && !collection.has(host)) temporary.push({ id, collection, registry });
        collection.add(host); registry.set(id, collection);
    };
    // The adopted envelope stays on its host for the readers that look it up there (`host._inventory_base`, `'_inventory_base' in host`),
    // but NON-enumerable: hydrated, it carries the shared catalog (~2.5 MB per inventory host), and every JSON deep clone of a row or of the
    // encounter (the Janus planners clone the whole encounter per dry run) copied it. fix-bug 2026-10-13: Roll Initiative froze the FT3D tab
    // ~110 s on a two-actor cell (2,035 clones / 8.1 GB of JSON before the start was posted). The runtime (`held`) stays the authority.
    const hold = (envelope: BaseEnvelope | BaseBusy): void => {
        Object.defineProperty(host, '_inventory_base', { value: envelope, enumerable: false, writable: true, configurable: true });
        adoptedSurfaces.set(host, envelope);
        if (owner) owner.inventory = envelope;
    };
    // This adoption answers for the envelopes the host carries now. A later adoption of the same host
    // (a fresher fetch, a patched member) replaces them; this one's failure is then obsolete.
    const owned = { entity: host._entity_base, inventory: host._inventory_base };
    const current = (): boolean => host._entity_base === owned.entity && host._inventory_base === owned.inventory;
    const encounterId = fromBaseRecord(owned.entity) && owned.entity.entity === 'encounter' && typeof owned.entity.entity_id === 'string'
        ? owned.entity.entity_id.toUpperCase() : '';
    const mutation = encounterReadGenerations.get(host) ?? startedMutation ?? encounterMutationGeneration(encounterId);
    // Catalog preload happens before a detached host joins the runtime. Identity
    // alone cannot fence it: a later wire adoption may already own canonical truth.
    const supersedingEncounter = async (): Promise<Row | undefined> => {
        if (!options.detached || !encounterId || encounterMutationGeneration(encounterId) === mutation) return;
        await entityRuntime.settled(encounterId);
        const canonical = window.FateTide?.encounterCache?.get(encounterId) as Row | undefined;
        if (!canonical || canonical === host || (encounterPaintGenerations.get(canonical) ?? -1) <= mutation
            || typeof canonical._entity_refusal === 'string' || typeof canonical._inventory_refusal === 'string'
            || !Array.isArray(canonical.encounter_creatures_arr) || entityRuntime.gap(encounterId) !== undefined) return;
        const envelope = canonical._entity_base as BaseEnvelope | undefined;
        if (envelope && entityRuntime.isCurrent(envelope) && fromBaseRecord(owned.entity)
            && envelope.base_rev >= Number(owned.entity.base_rev ?? 0)) return canonical;
    };
    try {
        let hydratedCatalogs: Map<BaseEnvelope | BaseBusy, BaseEnvelope | BaseBusy>;
        try { hydratedCatalogs = await preloadCatalogs(host); }
        catch (error) {
            if (window.ftHandleCompileEngineMismatch?.(error)) return;
            const superseded = await supersedingEncounter();
            if (superseded) return superseded;
            if (host._entity_base || host._inventory_base) {
                if (!current()) return;
                const facet = host._entity_base ? 'entity' : 'inventory';
                const reason = `unported:${facet}-catalog:${error instanceof Error ? error.message : 'read-failed'}`;
                if (facet === 'entity') host._entity_refusal = reason;
                host._inventory_refusal = reason;
                refuseUnported(reason, undefined, undefined, `${facet}-catalog:read-failed`); return;
            }
            throw error;
        }
        const superseded = await supersedingEncounter();
        if (superseded) return superseded;
        if (adoptionJobs.get(host) !== owner) return;
        const entity = host._entity_base as BaseEnvelope | BaseBusy | undefined;
        if (entity && adoptedSurfaces.get(host) !== entity) {
            const id = entity.entity_id.toUpperCase();
            register(id, entitySurfaces);
            const adopting = adoptingEntitySurfaces.get(id) ?? new Set<Row>();
            adopting.add(host); adoptingEntitySurfaces.set(id, adopting);
            // Entity paint also registers the inventory facet; include it in the planning copy's lifetime.
            if (options.transient) register(id);
            let hydrated: BaseEnvelope | BaseBusy | undefined;
            try {
                hydrated = hydratedCatalogs.get(entity) ?? await withCatalog(entity);
                const previous = entityRuntime.get(id, entity.entity);
                Object.defineProperty(host, '_entity_base', { value: hydrated, enumerable: false, writable: true, configurable: true }); adoptedSurfaces.set(host, hydrated);
                if (owner) owner.entity = hydrated;
                await entityRuntime.adopt(hydrated);
                // An older worker can finish while a newer revision still owns
                // this surface. Join that revision before inspecting its placeholder.
                await entityRuntime.settled(id, entity.entity);
                if (typeof host._entity_refusal === 'string' && !('resync' in hydrated) && entityRuntime.isCurrent(hydrated)) {
                    entitySurfaces.get(id)?.delete(host); surfaces.get(id)?.delete(host); adoptedSurfaces.delete(host);
                    if (previous) { entityRuntime.held.set(baseEntityKey(id, entity.entity), previous); await entityRuntime.adopt(previous, false); }
                    else entityRuntime.held.delete(baseEntityKey(id, entity.entity));
                }
            } catch (error) {
                if (window.ftHandleCompileEngineMismatch?.(error)) return;
                // A newer adoption of this host owns its surface now; this failure answers for a replaced envelope.
                if (host._entity_base !== (hydrated ?? entity)) return;
                adoptedSurfaces.delete(host);
                const reason = `unported:entity-catalog:${error instanceof Error ? error.message : 'read-failed'}`;
                host._entity_refusal = reason; host._inventory_refusal = reason;
                refuseUnported(reason, { entityId: id }, undefined, 'entity-catalog:read-failed'); return;
            } finally {
                adopting.delete(host);
                if (!adopting.size && adoptingEntitySurfaces.get(id) === adopting) adoptingEntitySurfaces.delete(id);
            }
        } else if (entity) await entityRuntime.settled(entity.entity_id, entity.entity);
        const carried = host._inventory_held;
        if (fromBaseRecord(carried) && typeof carried.entity_id === 'string') {
            const id = carried.entity_id.toUpperCase(), previous = runtime.get(id, 'inventory');
            register(id);
            if (previous && previous.base_rev === carried.base_rev && carried.engine === engine) {
                hold(previous);
                await runtime.adopt(previous, false);
            }
            else await runtime.resync(id);
            await runtime.settled(id);
            delete host._inventory_held;
        }
        const envelope = host._inventory_base as BaseEnvelope | BaseBusy | undefined;
        if (envelope && adoptedSurfaces.get(host) !== envelope) {
            const id = envelope.entity_id.toUpperCase();
            register(id);
            try {
                const hydrated = hydratedCatalogs.get(envelope) ?? await withCatalog(envelope);
                hold(hydrated);
                await runtime.adopt(hydrated);
                // A superseding revision may own this facet; its paint clears this host's placeholder too.
                await runtime.settled(id);
            }
            catch (error) { adoptedSurfaces.delete(host); throw error; }
        } else if (envelope) await runtime.settled(envelope.entity_id);
        for (const name of ['encounter_creatures_arr', 'location_encounter']) {
            if (Array.isArray(host[name])) await Promise.all((host[name] as unknown[]).map(child => adoptInventoryBase(child, { transient: options.transient })));
        }
    } finally {
        for (const { id, collection, registry } of temporary) {
            collection.delete(host);
            if (!collection.size && registry.get(id) === collection) registry.delete(id);
        }
        // A planning copy can later be installed as a canonical host; that adoption must register/paint it again.
        if (temporary.length) adoptedSurfaces.delete(host);
        if (transientMembers) releaseMemberInventories(host);
    }
}

/** A fetch overtaken by a wire frame is never a mounted surface. Retire its raw and nested hosts. */
export function discardInventoryBase(host: unknown): void {
    if (!fromBaseRecord(host)) return;
    releaseMemberInventories(host);
    if (memberInventoryOwners.get(host)?.size) return;
    for (const registry of [surfaces, entitySurfaces]) for (const [id, collection] of registry) {
        collection.delete(host);
        if (!collection.size) registry.delete(id);
    }
    adoptedSurfaces.delete(host);
    for (const name of ['encounter_creatures_arr', 'location_encounter'])
        if (Array.isArray(host[name])) for (const child of host[name]) discardInventoryBase(child);
}

/** Announce installed caster truth, moving its registration when the canonical row is a different object. */
function rebindEntityBase(source: unknown, host: unknown): void {
    if (!fromBaseRecord(source) || !fromBaseRecord(host)) return;
    const envelope = adoptedSurfaces.get(source);
    if (!envelope || 'resync' in envelope || source._entity_base !== envelope || typeof source._entity_refusal === 'string') return;
    if (source === host) { announceEntityCompiled(envelope, host); return; }
    const id = envelope.entity_id.toUpperCase();
    Object.defineProperty(host, '_entity_base', Object.getOwnPropertyDescriptor(source, '_entity_base')!);
    adoptedSurfaces.set(host, envelope);
    for (const registry of [surfaces, entitySurfaces]) {
        const collection = registry.get(id);
        if (collection?.has(source)) collection.add(host);
    }
    discardInventoryBase(source);
    announceEntityCompiled(envelope, host);
}

/** The newest base revision this page has heard of per entity: on the wire, before either facet applies or compiles it (the entity
 *  facet waits behind the inventory one), or held. A page's own optimistic edit made now is newer than every paint at or below it. */
const heardRevisions = new Map<string, number>();
function heardBaseRevision(entityId: string): number | undefined {
    const id = String(entityId ?? '').toUpperCase();
    const revisions = [heardRevisions.get(id) ?? -1, ...[...entityRuntime.held.values()].filter(envelope => envelope.entity_id.toUpperCase() === id).map(envelope => envelope.base_rev)];
    const newest = Math.max(...revisions);
    return newest >= 0 ? newest : undefined;
}
declare global { interface Window {
    ftBaseHeardRevision?: typeof heardBaseRevision;
    ftBaseAppliedRevision?: (entityId: string) => number | undefined;
    ftCorrectSavedRows?: (id: string, rows: readonly AppliedTouchedRow[], correction?: SavedRowCorrection) => void;
} }
window.ftBaseHeardRevision = heardBaseRevision;
window.ftBaseAppliedRevision = entityId => appliedRevisions.get(entityId.toUpperCase());
function correctSavedRows(id: string, rows: readonly AppliedTouchedRow[], correction?: SavedRowCorrection): void {
    runtime.correctSavedRows(id, rows, correction);
    entityRuntime.correctSavedRows(id, rows, correction);
    // A prepared adoption may own the runtime while the page still holds its
    // previous envelope. Raw CAS readers must take the same corrected input now.
    for (const [key, owner, registry] of [['_entity_base', entityRuntime, entitySurfaces], ['_inventory_base', runtime, surfaces]] as const) {
        for (const host of registry.get(id.toUpperCase()) ?? []) {
            const previous = host[key] as BaseEnvelope | undefined;
            if (!previous || 'resync' in previous) continue;
            const current = owner.get(id, previous.entity);
            if (!current || current === previous) continue;
            Object.defineProperty(host, key, { value: current, enumerable: false, writable: true, configurable: true });
            if (key === '_entity_base') adoptedSurfaces.set(host, current);
        }
    }
}
window.ftCorrectSavedRows = correctSavedRows;

/** Finish the actor's held definition closure before the transport encodes it. */
function prepareAppliedDefinitionInputs(frame: AppliedFrameResult): AppliedFrameResult {
    const ids = appliedDefinitionIds(frame);
    if (!ids.size) return frame;
    const encounter = window.FateTide?.encounterCache?.get(frame.encounter_id.toUpperCase()) as Row | undefined;
    const hosts = [encounter, ...Object.keys(frame.member_revs).map(id => window.FateTide?.casterCache?.get(id)),
        ...(Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])].filter(fromBaseRecord);
    const store = (window as unknown as { __ftDefinitionStore?: { peek?(id: string): { def: Row } | null } }).__ftDefinitionStore;
    return withAppliedDefinitionInputs(frame, id => heldDefinitionInputs(id, engine)
        ?? hosts.map(host => (host._entity_base as BaseEnvelope | undefined)?.base)
            .find(base => fromBaseRecord(base?.catalog) && fromBaseRecord(base.catalog[id])),
    id => [window.FateTide?.objectCache?.get(id), store?.peek?.(id)?.def,
        ...hosts.map(host => fromBaseRecord(host.catalog) ? host.catalog[id] : undefined)].find(fromBaseRecord),
    (encounter?._entity_base as BaseEnvelope | undefined)?.base ?? {}, ids);
}
declare global { interface Window { ftPrepareAppliedDefinitionInputs?: typeof prepareAppliedDefinitionInputs } }
window.ftPrepareAppliedDefinitionInputs = prepareAppliedDefinitionInputs;

/** A local cast already holds its status definitions. Carry their raw closure
 * into both facets before install, rather than recovering the participant. */
async function holdAppliedStatusInputs(frame: AppliedFrameResult): Promise<AppliedFrameResult> {
    if ('cache_ref' in frame) return frame;
    const ids = [...new Set(frame.rows.filter(row => row.table === 'statuses')
        .map(row => String(readItemField(row.row, 'status_item_id') ?? '').toUpperCase()).filter(Boolean))];
    if (!ids.length) return frame;
    const base = { catalog: frame.inputs?.catalog ?? {} };
    const source = (id: string): Row | undefined => {
        const raw = heldDefinitionInputs(id, engine);
        if (raw) return raw;
        for (const envelope of entityRuntime.held.values())
            if (fromBaseRecord(envelope.base.catalog) && fromBaseRecord(envelope.base.catalog[id])) return envelope.base;
        return undefined;
    };
    const held = await memberDefinitionInputs(base, ids, source, async () => {});
    return held === base ? frame : { ...frame, inputs: { ...frame.inputs, catalog: held.catalog } };
}

export async function handleBasePatch(input: import('../janus-engine/compile/base-patch').AppliedBasePatchFrame, actor = false): Promise<void> {
    // Applied installs supply the decoded argument to both runtimes; their wire
    // parameter is opaque on this path. Saved-row frames keep the wire contract.
    let frame = input as BasePatchFrame;
    if (frame.applied) {
        // Classifiers borrow this decode; each facet and painted host owns what it adopts.
        markFt3dEventDelayDiagnostic('parent.truth.install-start', frame.applied);
        const installed = await holdAppliedStatusInputs(parsedAppliedFrame(frame.applied));
        markFt3dEventDelayDiagnostic('parent.truth.status-inputs-ready', installed);
        await runtime.apply(frame, actor, installed);
        markFt3dEventDelayDiagnostic('parent.truth.inventory-installed', installed);
        await entityRuntime.apply(frame, actor, installed);
        markFt3dEventDelayDiagnostic('parent.truth.entity-installed', installed);
        if (!('cache_ref' in installed)) window.ftBankAppliedFrame?.(installed);
        window.dispatchEvent(new CustomEvent('ft:base-applied', { detail: { wire: installed } }));
        markFt3dEventDelayDiagnostic('parent.truth.install-end', installed);
        return;
    }
    // The relay names each delivered definition once per frame; every held host group reads it (before the embed filter below).
    frame = withSharedDefinitionOps(frame);
    for (const op of frame.ops) {
        // A full saved inventory echo proves persistence even if the POST answer
        // was lost. Partial equips and local applied previews are not birth proof.
        if (op.op === 'upsert' && op.table === 'inventory' && readInventoryField({ inventory: op.cols }, 'inventory_item_id')
            && readInventoryField({ inventory: op.cols }, 'possess_id') && !Number(readInventoryField({ inventory: op.cols }, 'inventory_deleted') ?? 0))
            confirmInventoryBirth(op.row_id);
        const id = op.entity_id.toUpperCase();
        if (Number.isSafeInteger(op.base_rev) && op.base_rev > (heardRevisions.get(id) ?? -1)) heardRevisions.set(id, op.base_rev);
    }
    const ids = new Set([...frame.ops.map(op => op.entity_id.toUpperCase()), ...(frame.resync ?? []).map(id => id.toUpperCase())]);
    const writer = actor ? true : new Set([...ids].filter(id => actorWrites.has(id)));
    await runtime.apply(localBaseFrame(runtime, frame), writer);
    await entityRuntime.apply(localBaseFrame(entityRuntime, frame), writer);
    projectCampaignPartyPlayers(frame.ops);
    for (const id of ids) actorWrites.delete(id);
}

function recordTouched(header: string | null, before: ReadonlyMap<string, number> = new Map(), revisionsHeader: string | null = null): void {
    const revisions = revisionsHeader ? JSON.parse(revisionsHeader) as Record<string, number> : undefined;
    if (revisions) {
        runtime.acknowledgeSavedRevisions(revisions);
        entityRuntime.acknowledgeSavedRevisions(revisions);
        for (const [id, revision] of Object.entries(revisions)) heardRevisions.set(id, Math.max(heardRevisions.get(id) ?? -1, revision));
    }
    const values = (header ?? '').split(',').filter(Boolean);
    const ids = new Set(values.filter(id => id !== 'more').map(id => id.toUpperCase()));
    // Overflow identifies a bulk write. Persist held facets without fetching unheld instances.
    if (values.includes('more')) for (const held of [runtime.held, entityRuntime.held]) for (const envelope of held.values()) ids.add(envelope.entity_id.toUpperCase());
    for (const id of ids) {
        actorWrites.add(id);
        const prior = before.get(id);
        // The optimistic install may already carry this save's revision when
        // the request starts. Its acknowledgement is exact; prior + 1 is only
        // the compatibility floor for responses without saved revisions.
        const revision = revisions?.[id] ?? (prior === undefined ? undefined : prior + 1);
        if (revision !== undefined) touchedMinimumRevisions.set(id, Math.max(touchedMinimumRevisions.get(id) ?? -1, revision));
    }
    // The websocket echo may have arrived before the HTTP response.
    void runtime.persistTouched([...ids]);
    void entityRuntime.persistTouched([...ids]);
}

// Pause every fetch-based write after a served engine change. Read-only POST seams remain available for recovery.
const fetchPage = window.fetch.bind(window);
function beginBaseWrite(method: string, url: string): { before?: ReadonlyMap<string, number>; finish(): void } {
    if (['GET', 'HEAD'].includes(method) || /\/(?:computed_cache_put_inc|spawn_inputs_inc)\.php(?:[?#]|$)/.test(url)) return { finish() {} };
    const before = new Map(heardRevisions);
    for (const held of [runtime.held, entityRuntime.held]) for (const envelope of held.values()) {
        const id = envelope.entity_id.toUpperCase();
        before.set(id, Math.max(before.get(id) ?? -1, envelope.base_rev));
    }
    return { before, finish: cacheWrites.beginAction() };
}
function encounterReadBody(input: RequestInfo | URL, init?: RequestInit): Row | undefined {
    const url = String(input instanceof Request ? input.url : input);
    if (!/\/get_encounter_arr\.php(?:[?#]|$)/.test(url) || typeof init?.body !== 'string') return;
    const body: unknown = JSON.parse(init.body);
    return fromBaseRecord(body) ? body : undefined;
}
/** Keep the HTTP read's fence on its decoded host, including time before catalog preload begins. */
function fetchWithEncounterFence<T extends SavedReadResponse>(input: RequestInfo | URL, init: RequestInit | undefined, read: () => Promise<T>): Promise<T> {
    const body = encounterReadBody(input, init);
    const mutation = body ? encounterMutationGeneration(body.encounter_id) : undefined;
    const pending = read();
    if (mutation === undefined) return pending;
    return pending.then(response => {
        const json = response.json.bind(response);
        response.json = async () => {
            const value: unknown = await json();
            if (fromBaseRecord(value)) encounterReadGenerations.set(value, mutation);
            return value;
        };
        return response;
    });
}
function prepareBaseRead(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
    const body = encounterReadBody(input, init);
    return body ? { ...init, body: JSON.stringify({ ...body, inventory_engine: engine,
        inventory_base_revs: Object.fromEntries([...runtime.held].map(([id, envelope]) => [id, envelope.base_rev])),
        inventory_catalog_versions: Object.fromEntries([...runtime.held].map(([id, envelope]) => [id, envelope.catalog_version])) }) } : init;
}
function observeSavedResponse<T extends SavedReadResponse>(response: T, method: string, before?: ReadonlyMap<string, number>): T {
    const served = response.headers.get('X-FT-Engine');
    if (served) { runtime.checkEngine(served); entityRuntime.checkEngine(served); }
    if (method !== 'GET' && method !== 'HEAD') {
        if (response.ok) recordTouched(response.headers.get('X-FT-Base-Touched'), before, response.headers.get('X-FT-Base-Revisions'));
    }
    return response;
}
(window as unknown as { ftFetchSavedResponse: typeof fetchSavedResponse }).ftFetchSavedResponse = async (input, init) => {
    const url = new URL(input, new URL(BASE_URL, location.href)).href;
    if (ftCurrentTruthSink() && isTruthRequest(url, init?.method)) return truthFetch(url, init, fetchPage);
    const method = String(init?.method ?? 'GET').toUpperCase(), action = beginBaseWrite(method, url);
    try {
        const response = await fetchWithEncounterFence(input, init, () => entityCompiler.read(url, prepareBaseRead(input, init)));
        return observeSavedResponse(response, method, action.before);
    } finally { action.finish(); }
};
window.fetch = (input, init) => {
    const method = String(init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = String(input instanceof Request ? input.url : input);
    if (ftCurrentTruthSink() && isTruthRequest(url, method)) {
        return input instanceof Request
            ? input.clone().text().then(body => truthFetch(url, { method: input.method, headers: input.headers, body, ...init }, fetchPage))
            : truthFetch(url, init, fetchPage);
    }
    const read = /\/(?:get_caster_arr|get_encounter_arr|object_inc)\.php(?:[?#]|$)/.test(url);
    init = prepareBaseRead(input, init);
    if ((runtime.paused || entityRuntime.paused) && method !== 'GET' && method !== 'HEAD' && !read) {
        return Promise.reject(new Error('New FateTide version: reload before committing'));
    }
    const action = beginBaseWrite(method, url);
    return fetchWithEncounterFence(input, init, () => fetchPage(input, init))
        .then(response => observeSavedResponse(response, method, action.before)).finally(action.finish);
};
if (typeof XMLHttpRequest !== 'undefined') {
    const methods = new WeakMap<XMLHttpRequest, { method: string; url: string }>();
    const open = XMLHttpRequest.prototype.open, send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, async: boolean = true, username?: string | null, password?: string | null): void {
        methods.set(this, { method: method.toUpperCase(), url: String(url) });
        open.call(this, method, url, async, username, password);
    };
    XMLHttpRequest.prototype.send = function (body): void {
        const request = methods.get(this);
        if ((runtime.paused || entityRuntime.paused) && request && !['GET', 'HEAD'].includes(request.method)
            && !/\/(?:get_caster_arr|get_encounter_arr|object_inc)\.php(?:[?#]|$)/.test(request.url)) {
            throw new Error('New FateTide version: reload before committing');
        }
        const action = beginBaseWrite(request?.method ?? 'GET', request?.url ?? '');
        this.addEventListener('loadend', action.finish, { once: true });
        this.addEventListener('load', () => {
            const served = this.getResponseHeader('X-FT-Engine');
            if (served) runtime.checkEngine(served);
            if (served) entityRuntime.checkEngine(served);
            if (request && !['GET', 'HEAD'].includes(request.method) && this.status >= 200 && this.status < 300)
                recordTouched(this.getResponseHeader('X-FT-Base-Touched'), action.before, this.getResponseHeader('X-FT-Base-Revisions'));
        }, { once: true });
        try { send.call(this, body); } catch (error) { action.finish(); throw error; }
    };
}
document.addEventListener('submit', event => {
    if (runtime.paused || entityRuntime.paused) { event.preventDefault(); event.stopImmediatePropagation(); }
}, true);
window.ftRequireEntityBase = (id: string, announce = true): boolean => {
    if (runtime.paused || entityRuntime.paused) {
        if (announce) refuseUnported('engine-version-reload', { entityId: id });
        else noteEngineReload({ entityId: id });
        return false;
    }
    const key = id.toUpperCase();
    const hosts = [...(entitySurfaces.get(key) ?? [])];
    for (const cache of [window.FateTide?.casterCache, window.FateTide?.encounterCache, window.FateTide?.objectCache]) {
        const host = cache?.get(key);
        if (fromBaseRecord(host) && host._entity_base) hosts.push(host);
    }
    for (const encounter of window.FateTide?.encounterCache?.values() ?? [])
        for (const member of encounter.encounter_creatures_arr ?? [])
            if (String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id')).toUpperCase() === key) hosts.push(member as Row);
    if (entityRuntime.paused) { versionChanged(); return false; }
    // A required revision an exhausted recovery could not reach stays outstanding until a read meets it;
    // a painted surface below it is stale, not ready.
    const owner = entityRuntime.gap(key) !== undefined ? entityRuntime : runtime.gap(key) !== undefined ? runtime : null;
    if (owner) {
        const required = owner.gap(key)!;
        refuseUnported(`unported:entity-base:base-revision-gap:${required}`, { entityId: id, required });
        return false;
    }
    // A surface still wearing PHP's pending placeholder while its adoption runs is not refused: the
    // boundary awaits that adoption (`ftAwaitEntityBase`) and reads the painted surface or a named refusal.
    const adopting = (): boolean => pendingBirths.has(key) || inFlight(key) || owningEncounters(key).some(inFlight);
    if (pendingBirths.has(key)) return false;
    const failed = hosts.find(host => typeof host._entity_refusal === 'string'
        && !(PENDING_PLACEHOLDER.test(host._entity_refusal) && (paintedEntities.has(key) || adopting())));
    if (failed) { reportEntityRefusal(id, String(failed._entity_refusal), announce); return false; }
    return true;
};
window.ftAwaitEntityBase = awaitEntityBase;
function inventoryBaseState(id: string): 'ready' | 'pending' | 'unavailable' {
    const hosts = surfaces.get(id.toUpperCase());
    if (runtime.paused || entityRuntime.paused) return 'unavailable';
    if (!hosts?.size) return inFlight(id) ? 'pending' : 'unavailable';
    const reasons = [...hosts].map(host => host._inventory_refusal).filter(reason => typeof reason === 'string');
    if (reasons.some(reason => reason !== 'unported:inventory-base-pending')) return 'unavailable';
    return reasons.length ? 'pending' : 'ready';
}

/** A successful move can be followed by a newer caster snapshot still adopting its catalog. */
async function waitForInventoryBase(id: string): Promise<void> {
    const until = Date.now() + 15000;
    while (inventoryBaseState(id) === 'pending') {
        const jobs = pendingFor([id.toUpperCase()]);
        if (jobs.length) { await Promise.allSettled(jobs); continue; }
        if (Date.now() >= until) return;
        await new Promise<void>(resolve => {
            const finish = (): void => {
                clearTimeout(deadline);
                window.removeEventListener('ft:inventory-base-compiled', changed);
                window.removeEventListener('ft:inventory-base-refused', changed);
                resolve();
            };
            const changed = (event: Event): void => {
                const entityId: unknown = (event as CustomEvent).detail?.entityId;
                if (typeof entityId === 'string' && entityId.toUpperCase() === id.toUpperCase()) finish();
            };
            // Active adoption/worker requests have their own named refusals.
            // This deadline covers a pending marker with no work behind it.
            const deadline = setTimeout(finish, Math.max(0, until - Date.now()));
            window.addEventListener('ft:inventory-base-compiled', changed);
            window.addEventListener('ft:inventory-base-refused', changed);
            if (inventoryBaseState(id) !== 'pending') finish();
        });
    }
}

Object.assign(hostWindow, { ftAdoptInventoryBase: adoptInventoryBase, ftDiscardInventoryBase: discardInventoryBase,
    ftPreloadEntityCatalogs: preloadEntityCatalogs,
    ftCompileRelocationMembers: async (envelopes: readonly BaseEnvelope[], destination?: Row) => {
        const failures: import('../janus-engine/compile/saved-creature-relocation').RelocationMemberFailure[] = [];
        const complete = (await Promise.all(envelopes.map(async envelope => {
            try {
                const full = await withCatalog(envelope);
                if ('resync' in full) throw new Error('relocation-member-batch:base-busy');
                if (!full.metadata) throw new Error('relocation-member-batch:metadata');
                return full;
            } catch (error) {
                failures.push({ id: envelope.entity_id, reason: error instanceof Error ? error.message : 'relocation-member-batch:read' });
                return null;
            }
        }))).filter((envelope): envelope is BaseEnvelope => envelope !== null);
        const entities = complete.map(envelope => {
            const source = envelope.base.encounter_creatures as Row;
            const revision = envelope.base_rev + (destination ? 1 : 0);
            const dependencies = { ...envelope.base.dependency_revs as Row };
            if (destination) {
                delete dependencies[String(encounterCreatureField(source, 'encounter_id') ?? '').toUpperCase()];
                dependencies[envelope.entity_id.toUpperCase()] = revision;
                dependencies[String(encounterField(destination, 'encounter_id')).toUpperCase()] = Number(destination.base_rev ?? 0) + 1;
            }
            const base = destination ? { ...mergeInventoryMetadata(envelope.base, encounterMemberSharedInputs(destination)), relocation_before: source,
                base_rev: revision, dependency_revs: dependencies,
                ...(typeof envelope.base.catalog_ref === 'string' ? { catalog_ref: envelope.base.catalog_ref.replace(/:\d+$/, `:${revision}`) } : {}),
                inventory_order: envelope.base.inventory_order,
                encounter_context: { encounters: destination.encounters, campaign_arr: destination.campaign_arr },
                encounter_creatures: { ...source, encounter_id: encounterField(destination, 'encounter_id'),
                    movement_rev: Number(encounterCreatureField(source, 'movement_rev') ?? 0) + 1 } } : envelope.base;
            return { entity: 'creature' as const, entity_id: envelope.entity_id, base_rev: revision, base, member: {} };
        });
        const batch = complete.length ? await compileRelocationBatch(entities, complete[0]!.metadata as import('../janus-engine/spawn/source-plan').SpawnSourcePackage['metadata'])
            : { members: [], failures: [] };
        return { members: batch.members, failures: [...failures, ...batch.failures] };
    },
    ftGetEntitySharedCatalog: (version: string) => globalCatalogs.has(version)
        ? Promise.resolve(globalCatalogs.get(version)!) : requestCatalog(version, ''),
    ftHeldEntityCatalogHints: heldEntityCatalogHints,
    ftRefusalSnapshotInputs: refusalSnapshotInputs,
    ftRebindEntityBase: rebindEntityBase, ftApplyBasePatch: handleBasePatch,
    ftHeldCompileEngine: () => runtime.held.values().next().value?.engine ?? entityRuntime.held.values().next().value?.engine ?? null,
    ftBaseCommitsPaused: () => runtime.paused || entityRuntime.paused, ftWaitForInventoryBase: waitForInventoryBase,
    ftReadHeldInventoryBase: (id: string) => runtime.get(id, 'inventory'),
    ftReadHeldEntityBase: (id: string, entity = 'encounter') => entityRuntime.get(id, entity) });
Object.assign(window, { ftAppliedTreeMetrics: appliedTreeMetrics, ftAppliedFramePlanMetrics: appliedFramePlanMetrics,
    ftEntitySurfacePreparationMetrics: entitySurfacePreparationMetrics });
window.ftBaseRuntimeStats = () => ({ inventory: runtime.stats(), entity: entityRuntime.stats(), members: entityCompiler.stats(),
    memberCompiles: entityCompiler.memberCompiles(), worker: entityCompiler.transportStats(), skillGrantWork: { ...skillGrantWork } });
window.ftRequireInventoryBase = (id: string, announce = true): boolean => {
    if (runtime.paused || entityRuntime.paused) {
        if (announce) refuseUnported('engine-version-reload', { entityId: id });
        else noteEngineReload({ entityId: id });
        return false;
    }
    if (inventoryBaseState(id) !== 'ready') {
        if (runtime.paused) versionChanged();
        else if (announce) refuseUnported('inventory-base-unavailable', { entityId: id });
        return false;
    }
    return true;
};
const adoptCaches = (): void => {
    for (const cache of [window.FateTide?.casterCache, window.FateTide?.encounterCache, window.FateTide?.objectCache]) {
        if (cache instanceof Map) for (const host of cache.values()) void adoptInventoryBase(host);
    }
};
const preparationMetadata = new WeakMap<object, ReturnType<typeof entityRuntimeMetadata>>();
// Cross-bundle, read-only borrowing of the member's held raw form sources.
(window as Window & { ftFormRevertSource?: (id: string) => { base: Row; metadata: Row } | undefined }).ftFormRevertSource = id => {
    const held = entityRuntime.memberContext(id);
    return held && fromBaseRecord(held.envelope.metadata) ? { base: held.base, metadata: held.envelope.metadata } : undefined;
};
/** Projection-only frames borrow a plan; only the raw owner installs saved sections. */
async function patchPreparedSpellDelta(id: string, sections: Row, current: () => boolean = () => true, projection?: Row, savedInputs?: { patch: (base: Row) => Row; definitions: string[] }): Promise<Omit<PreparedSpellDelta, 'saved'> | undefined> {
    const held = entityRuntime.memberContext(id);
    if (!held || !held.envelope.metadata) return;
    const initialBase = held.envelope.base;
    const rawMetadata = held.envelope.metadata;
    let metadata = preparationMetadata.get(rawMetadata);
    if (!metadata) {
        metadata = entityRuntimeMetadata(rawMetadata as Parameters<typeof entityRuntimeMetadata>[0]);
        preparationMetadata.set(rawMetadata, metadata);
    }
    let base = held.base;
    const ids = [...preparedSpellRows(sections.prepared).map(row => String(row.spell_id)), ...savedInputs?.definitions ?? []];
    const store = (window as unknown as { __ftDefinitionStore?: { peek?(id: string): { def: Row } | null } }).__ftDefinitionStore;
    const input = (id: string): Row | undefined => {
        const def = definitions.get(held.envelope.catalog_version ?? '')?.[id];
        const source = fromBaseRecord(def) ? definitionInputs.get(def) : undefined;
        if (source) return source;
        const row = window.FateTide?.objectCache?.get(id) ?? store?.peek?.(id)?.def;
        return (row as Row | undefined)?._entity_base ? ((row as Row)._entity_base as BaseEnvelope).base : undefined;
    };
    base = await memberDefinitionInputs(base, ids, input, ids => fetchMemberDefinitions(ids, held.envelope.catalog_version));
    if (!current()) return;
    const latest = entityRuntime.memberContext(id);
    if (!latest) return;
    if (latest.envelope.base !== initialBase || latest.sectionRevision !== held.sectionRevision)
        base = await memberDefinitionInputs(latest.base, ids, input, ids => fetchMemberDefinitions(ids, latest.envelope.catalog_version));
    if (!current()) return;
    if (savedInputs && !projection) {
        const inputs = base;
        entityRuntime.patchMemberSections(id, (prior, computed) => ({
            base: savedInputs.patch({ ...prior, ...encounterMemberSharedInputs(inputs) }), computed,
        }));
        base = entityRuntime.memberContext(id)?.base ?? base;
        if (!('prepared' in sections)) return;
    }
    const delta = planPreparedSpellDelta(base as unknown as FromBasePlayerInput, metadata.player, projection ?? latest.computed, sections);
    if (projection) return { sections: delta.sections, catalog: delta.catalog, spellCompiles: delta.spellCompiles };
    entityRuntime.patchMemberSections(id, (prior, computed) => ({
        base: { ...prior, ...encounterMemberSharedInputs(base), encounter_creatures_spells_prepared: delta.saved.encounter_creatures_spells_prepared,
            encounter_creatures: { ...prior.encounter_creatures as Row,
                spell_list_prepared: sections.spell_list_prepared,
                movement_rev: Math.max(Number(encounterCreatureField(prior.encounter_creatures, 'movement_rev') ?? 0), Number(sections.movement_rev ?? 0)) } },
        computed: patchPreparedSpellProjection(computed, delta),
    }), delta.catalog);
    return delta;
}
declare global { interface Window { ftPatchPreparedSpellDelta?: typeof patchPreparedSpellDelta } }
window.ftPatchPreparedSpellDelta = patchPreparedSpellDelta;
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', adoptCaches, { once: true });
else adoptCaches();

// UI bundles share this fenced compiler through window instead of importing its runtime graph.
Object.assign(window, { ftValidateEnchantment: validateEnchantment });
